import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { formatSeenAgo } from '../../utils/relativeTime';
import ActiveStatus, { isUserOnline } from '../ActiveStatus';
import UserAvatar, { UserNameLink, useProfilePrefetchHandlers } from '../UserAvatar';
import { useMatchup } from '../../contexts/MatchupContext';
import { useTheme } from '../../contexts/ThemeContext';

const ACTIVE_BATTLE_BLOCK_MESSAGE = "You're already in a battle — finish it before inviting someone else.";

function timeAgo(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(diff) || diff < 0) return '';
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'now';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d`;
}

// Detect an in-progress "@handle" the caret is currently sitting inside, so the
// composer can pop an Instagram-style mention picker. Returns the partial
// handle plus the [start,end) span of the "@handle" token (start points at the
// "@"), or null when the caret isn't on a mention. The "@" must start the line
// or follow whitespace so emails / mid-word "@" don't trigger it.
function detectMention(value, caret) {
  if (typeof value !== 'string') return null;
  const upToCaret = value.slice(0, caret);
  const m = upToCaret.match(/(?:^|\s)@([A-Za-z0-9_]{0,30})$/);
  if (!m) return null;
  const leftToken = m[1];
  // The caret can sit in the MIDDLE of an existing handle, so extend the span
  // right to the end of the token. `token` (left of caret) drives the search;
  // [start,end) covers the whole "@handle" so replacement never leaves a
  // trailing fragment behind (e.g. "@jo|hn" → "@picked", not "@picked hn").
  const rightMatch = value.slice(caret).match(/^[A-Za-z0-9_]*/);
  const right = rightMatch ? rightMatch[0] : '';
  return {
    token: leftToken,
    start: caret - leftToken.length - 1,
    end: caret + right.length,
  };
}

// Local Avatar wrapper kept as a thin alias so older call sites continue to
// work; under the hood it renders the new shared UserAvatar with deterministic
// colored initials and optional profile linking.
function Avatar(props) {
  return <UserAvatar {...props} />;
}

// In-thread bell toggle for muting a friend's push notifications. Surfaces
// the same per-friend mute that lives on the Friends page, but right where
// the user is staring at the noisy person's messages. Self-fetches the
// initial state from GET /api/friends/:id/mute so it works regardless of
// whether the caller seeded `friend.pushMuted` (the conversations endpoint
// doesn't include it). Optimistic POST/DELETE with revert on failure.
function FriendMuteToggle({ friend }) {
  const seed = typeof friend?.pushMuted === 'boolean' ? friend.pushMuted : null;
  const [muted, setMuted] = useState(seed);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    // Reset to the latest seed whenever the friend changes; if the caller
    // didn't pass pushMuted, fall back to a fresh GET so we don't leak the
    // previous friend's state into the new thread.
    setMuted(typeof friend?.pushMuted === 'boolean' ? friend.pushMuted : null);
  }, [friend?.id, friend?.pushMuted]);

  useEffect(() => {
    if (!friend?.id) return undefined;
    if (typeof friend?.pushMuted === 'boolean') return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/friends/${friend.id}/mute`, { credentials: 'include' });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setMuted(!!data.muted);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [friend?.id, friend?.pushMuted]);

  if (!friend?.id || muted === null) return null;

  const handleToggle = async () => {
    if (pending) return;
    const next = !muted;
    setMuted(next);
    setPending(true);
    try {
      const res = await fetch(`/api/friends/${friend.id}/mute`, {
        method: next ? 'POST' : 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed');
    } catch {
      setMuted(!next);
    } finally {
      setPending(false);
    }
  };

  const label = muted ? 'Unmute notifications' : 'Mute notifications';

  return (
    <button
      type="button"
      onClick={handleToggle}
      disabled={pending}
      title={label}
      aria-label={label}
      aria-pressed={muted}
      className={`inline-flex items-center justify-center w-9 h-9 rounded-xl flex-shrink-0 transition-colors disabled:opacity-60 ${muted ? 'text-orange-400 hover:bg-orange-500/15' : 'text-gray-400 hover:bg-white/10'}`}
      style={{ WebkitTapHighlightColor: 'transparent' }}
    >
      {muted ? (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l4-4m0 4l-4-4" />
        </svg>
      ) : (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
      )}
    </button>
  );
}

// Per-message "•••" actions menu (Instagram-style). Replaces the old bare
// trashcan: tapping the three dots opens a small popover with "Unsend"
// (delete for everyone — own messages only) and "Delete for me" (hide from
// just this user's view). The trigger stays visible at low opacity on touch
// (no-hover preference) and reveals on hover on desktop.
function MessageActionsMenu({ mine, disabled, onUnsend, onDeleteForMe }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative flex-shrink-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        aria-label="Message actions"
        aria-haspopup="menu"
        aria-expanded={open}
        title="Message actions"
        className={`flex-shrink-0 inline-flex items-center justify-center rounded-full transition-opacity ${
          open ? 'opacity-100' : 'opacity-60 lg:opacity-0 lg:group-hover:opacity-80 lg:hover:opacity-100'
        }`}
        style={{
          width: 26,
          height: 26,
          background: 'rgba(15,23,42,0.6)',
          border: '1.5px solid rgba(148,163,184,0.35)',
          color: '#cbd5e1',
          cursor: disabled ? 'wait' : 'pointer',
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <circle cx="5" cy="12" r="1.7" />
          <circle cx="12" cy="12" r="1.7" />
          <circle cx="19" cy="12" r="1.7" />
        </svg>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 z-50 mt-1 rounded-xl overflow-hidden"
          style={{
            top: '100%',
            minWidth: 168,
            background: '#0f1116',
            border: '1px solid rgba(255,255,255,0.12)',
            boxShadow: '0 12px 32px rgba(0,0,0,0.6)',
          }}
        >
          {mine && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onUnsend();
              }}
              className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-left text-sm lg:hover:bg-white/5 transition-colors"
              style={{ color: '#f87171' }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M3 7v6h6" />
                <path d="M3 13a9 9 0 1 0 3-7.7L3 8" />
              </svg>
              <span className="font-semibold">Unsend</span>
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onDeleteForMe();
            }}
            className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-left text-sm lg:hover:bg-white/5 transition-colors"
            style={{ color: '#e2e8f0', borderTop: mine ? '1px solid rgba(255,255,255,0.06)' : 'none' }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              <path d="M10 11v6" />
              <path d="M14 11v6" />
            </svg>
            <span className="font-semibold">Delete for me</span>
          </button>
        </div>
      )}
    </div>
  );
}

// Scroll only the inner chat container — never call scrollIntoView, which can
// scroll the outer page if the chat body isn't itself the nearest scrollable
// ancestor. This fixes the long-standing scroll-hijack bug.
function scrollToBottom(scrollEl) {
  if (!scrollEl) return;
  scrollEl.scrollTop = scrollEl.scrollHeight;
}

const MAX_VOICE_MS = 60000;

function formatDuration(ms) {
  if (!Number.isFinite(ms) || ms <= 0) return '0:00';
  const total = Math.round(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

// Tiny static waveform used in the conversation list to preview a voice
// note without playback controls. Downsamples the persisted peaks array
// (typically 36 entries — see WAVEFORM_BAR_COUNT) to a smaller bar count
// so the visualization fits comfortably alongside the username + timestamp
// in a single inbox row. Bars inherit the surrounding text color via
// `currentColor`, so they automatically pick up the unread/read styling
// applied by the parent row.
const INBOX_WAVEFORM_BAR_COUNT = 22;

function downsamplePeaksForInbox(peaks, targetCount = INBOX_WAVEFORM_BAR_COUNT) {
  if (!Array.isArray(peaks) || peaks.length === 0) return [];
  if (peaks.length <= targetCount) return peaks.map((v) => Math.max(0, Math.min(1, Number(v) || 0)));
  const out = new Array(targetCount).fill(0);
  const step = peaks.length / targetCount;
  for (let i = 0; i < targetCount; i++) {
    let max = 0;
    const start = Math.floor(i * step);
    const end = Math.min(peaks.length, Math.floor((i + 1) * step) || start + 1);
    for (let j = start; j < end; j++) {
      const v = Math.abs(Number(peaks[j]) || 0);
      if (v > max) max = v;
    }
    out[i] = Math.max(0, Math.min(1, max));
  }
  return out;
}

function InboxVoicePreview({ peaks, durationMs, fromMe }) {
  const bars = useMemo(() => downsamplePeaksForInbox(peaks), [peaks]);
  if (bars.length === 0) return null;
  const durationLabel = formatDuration(durationMs);
  return (
    <span className="inline-flex items-center gap-1.5 align-middle">
      {fromMe ? <span className="flex-shrink-0">You:</span> : null}
      <span
        aria-hidden="true"
        className="inline-flex items-end gap-[1px] h-3 flex-shrink-0"
      >
        {bars.map((p, i) => (
          <span
            key={i}
            className="w-[2px] rounded-sm"
            style={{
              height: `${Math.max(15, Math.round(p * 100))}%`,
              backgroundColor: 'currentColor',
              opacity: 0.85,
            }}
          />
        ))}
      </span>
      <span className="flex-shrink-0 tabular-nums">{durationLabel}</span>
      <span className="sr-only">Voice message</span>
    </span>
  );
}

// Encode a (mono or stereo) AudioBuffer into a 16-bit PCM WAV blob. WAV is
// the only browser-native container we can synthesize without a third-party
// encoder, so we use it as the trimmed output format. For ~60s mono speech
// at 44.1 kHz this is roughly 5 MB, comfortably inside the 10 MB voice-note
// upload cap.
function audioBufferToWavBlob(buffer) {
  const numChannels = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const samples = buffer.length;
  const dataSize = samples * numChannels * 2;
  const ab = new ArrayBuffer(44 + dataSize);
  const view = new DataView(ab);
  let offset = 0;
  const writeStr = (s) => {
    for (let i = 0; i < s.length; i++) view.setUint8(offset++, s.charCodeAt(i));
  };
  const writeU32 = (v) => { view.setUint32(offset, v, true); offset += 4; };
  const writeU16 = (v) => { view.setUint16(offset, v, true); offset += 2; };
  writeStr('RIFF');
  writeU32(36 + dataSize);
  writeStr('WAVE');
  writeStr('fmt ');
  writeU32(16);
  writeU16(1); // PCM
  writeU16(numChannels);
  writeU32(sampleRate);
  writeU32(sampleRate * numChannels * 2);
  writeU16(numChannels * 2);
  writeU16(16);
  writeStr('data');
  writeU32(dataSize);
  const channelData = [];
  for (let c = 0; c < numChannels; c++) channelData.push(buffer.getChannelData(c));
  for (let i = 0; i < samples; i++) {
    for (let c = 0; c < numChannels; c++) {
      let s = Math.max(-1, Math.min(1, channelData[c][i]));
      s = s < 0 ? s * 0x8000 : s * 0x7fff;
      view.setInt16(offset, s, true);
      offset += 2;
    }
  }
  return new Blob([ab], { type: 'audio/wav' });
}

const WAVEFORM_BAR_COUNT = 36;

// Compute a small array of normalized waveform peaks (0..1) from a decoded
// AudioBuffer. Shared between the live preview decode (inside VoiceWaveform)
// and the trim re-encode path so the two emit identical-shaped peak arrays
// regardless of which one the bubble ends up rendering from.
function extractPeaksFromAudioBuffer(decoded, barCount = WAVEFORM_BAR_COUNT) {
  const channel = decoded.getChannelData(0);
  const samplesPerBar = Math.max(1, Math.floor(channel.length / barCount));
  const out = new Array(barCount).fill(0);
  let max = 0;
  for (let b = 0; b < barCount; b++) {
    let peak = 0;
    const start = b * samplesPerBar;
    const end = Math.min(channel.length, start + samplesPerBar);
    for (let i = start; i < end; i++) {
      const v = Math.abs(channel[i]);
      if (v > peak) peak = v;
    }
    out[b] = peak;
    if (peak > max) max = peak;
  }
  return max > 0 ? out.map((v) => Math.min(1, (v / max) * 1.1)) : out;
}

// Decode every segment in `segments` (in order) and stitch their PCM
// samples into a single WAV blob. Used by the Safari/iOS fallback "Continue"
// path: when MediaRecorder can't pause+requestData to keep one take alive
// across a preview, we instead stop the recorder, start a fresh one when
// the user taps Continue, and then concatenate the resulting blobs into one
// playable clip on send. Returns null if the environment can't decode
// audio (e.g. AudioContext is unavailable) so callers can fall back to the
// most-recent segment.
//
// We assume segments share a sample rate (true in practice — they're from
// successive getUserMedia calls on the same device, with no constraints
// changes between them). Channel counts are reconciled by mixing each
// segment up to the widest count via channel duplication, which is correct
// for mono→stereo and good enough for the rare reverse case.
async function concatVoiceSegmentsToWav(segments) {
  if (typeof window === 'undefined') return null;
  if (!segments || !segments.length) return null;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  const ctx = new Ctx();
  try {
    const decoded = [];
    for (const seg of segments) {
      const arrBuf = await seg.blob.arrayBuffer();
      // decodeAudioData consumes the buffer, so slice() per-segment so a
      // failure in segment N doesn't poison segments 0..N-1.
      const buf = await ctx.decodeAudioData(arrBuf.slice(0));
      decoded.push(buf);
    }
    const sampleRate = decoded[0].sampleRate;
    const numChannels = decoded.reduce(
      (m, b) => Math.max(m, b.numberOfChannels),
      1,
    );
    const totalLength = decoded.reduce((s, b) => s + b.length, 0);
    if (totalLength <= 0) return null;
    const out = ctx.createBuffer(numChannels, totalLength, sampleRate);
    for (let c = 0; c < numChannels; c++) {
      const dest = out.getChannelData(c);
      let offset = 0;
      for (const b of decoded) {
        const srcChan = c < b.numberOfChannels ? c : 0;
        dest.set(b.getChannelData(srcChan), offset);
        offset += b.length;
      }
    }
    return audioBufferToWavBlob(out);
  } finally {
    try { await ctx.close(); } catch {}
  }
}

// Decode `blob`, slice the [startMs, endMs] window, and re-encode it as a
// new WAV blob. Also returns the same kind of waveform peaks the preview
// captured so the trimmed bubble can render its visualizer instantly on
// the receiver's side without a second decode. Returns null if the
// environment can't decode audio (e.g. AudioContext is unavailable) so
// callers can fall back to the original blob.
async function trimVoiceBlobToWav(blob, startMs, endMs) {
  if (typeof window === 'undefined') return null;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  const arrBuf = await blob.arrayBuffer();
  const ctx = new Ctx();
  let decoded;
  try {
    decoded = await ctx.decodeAudioData(arrBuf.slice(0));
  } catch (err) {
    try { await ctx.close(); } catch {}
    throw err;
  }
  const sampleRate = decoded.sampleRate;
  const startSample = Math.max(0, Math.floor((startMs / 1000) * sampleRate));
  const endSample = Math.min(
    decoded.length,
    Math.ceil((endMs / 1000) * sampleRate),
  );
  const length = Math.max(0, endSample - startSample);
  if (length <= 0) {
    try { await ctx.close(); } catch {}
    return null;
  }
  const numChannels = decoded.numberOfChannels;
  const trimmed = ctx.createBuffer(numChannels, length, sampleRate);
  for (let c = 0; c < numChannels; c++) {
    const src = decoded.getChannelData(c);
    trimmed.getChannelData(c).set(src.subarray(startSample, endSample));
  }
  let peaks = null;
  try { peaks = extractPeaksFromAudioBuffer(trimmed); } catch { peaks = null; }
  try { await ctx.close(); } catch {}
  return { blob: audioBufferToWavBlob(trimmed), peaks };
}

// Smallest allowed gap between the trim start and trim end handles. Anything
// shorter than this is functionally a zero-length clip, so the handles refuse
// to cross or sit on top of each other.
const MIN_TRIM_MS = 200;

// Module-level coordination so only one voice clip plays at a time across the
// messenger (sent bubbles, received bubbles, and the composer preview row all
// share the same registry). When a `VoiceWaveform` starts playback it calls
// `claimVoicePlayback(audioEl)`; if a different audio element was previously
// playing we pause it, which fires its native `pause` event and flips its
// bubble UI back to the play state. Because we never touch `currentTime` the
// paused clip keeps its scrub position so the user can resume where they left
// off by tapping play again.
let currentVoicePlayer = null;

function claimVoicePlayback(audioEl) {
  if (!audioEl) return;
  if (currentVoicePlayer && currentVoicePlayer !== audioEl) {
    try { currentVoicePlayer.pause(); } catch {}
  }
  currentVoicePlayer = audioEl;
}

function releaseVoicePlayback(audioEl) {
  if (currentVoicePlayer === audioEl) currentVoicePlayer = null;
}

// In-memory LRU cache of decoded waveform peaks keyed by attachment URL.
// Decoding the same audio file every time a bubble re-mounts (e.g. while
// scrolling a long thread, or after toggling another panel) is wasteful —
// the source bytes don't change, so the resulting peak array doesn't either.
// We cap the cache so very long sessions don't grow unbounded; the oldest
// entry is dropped when we exceed the cap. JS Maps preserve insertion order,
// which gives us O(1) LRU updates by deleting + re-setting on access.
const WAVEFORM_PEAK_CACHE = new Map();
const WAVEFORM_PEAK_CACHE_MAX = 128;

function getCachedWaveformPeaks(key) {
  if (!key) return null;
  if (!WAVEFORM_PEAK_CACHE.has(key)) return null;
  const peaks = WAVEFORM_PEAK_CACHE.get(key);
  // Refresh recency: delete + re-insert so this key becomes the newest.
  WAVEFORM_PEAK_CACHE.delete(key);
  WAVEFORM_PEAK_CACHE.set(key, peaks);
  return peaks;
}

function setCachedWaveformPeaks(key, peaks) {
  if (!key || !peaks) return;
  if (WAVEFORM_PEAK_CACHE.has(key)) WAVEFORM_PEAK_CACHE.delete(key);
  WAVEFORM_PEAK_CACHE.set(key, peaks);
  while (WAVEFORM_PEAK_CACHE.size > WAVEFORM_PEAK_CACHE_MAX) {
    const oldest = WAVEFORM_PEAK_CACHE.keys().next().value;
    if (oldest === undefined) break;
    WAVEFORM_PEAK_CACHE.delete(oldest);
  }
}

// Schedule low-priority work (e.g. the legacy waveform warm-up decode) so it
// runs after the thread has finished its initial render rather than racing
// every other voice bubble for bandwidth the moment a thread opens. Falls
// back to a short setTimeout on browsers without requestIdleCallback (Safari).
function scheduleIdle(cb) {
  if (typeof window === 'undefined') return { cancel() {} };
  if (typeof window.requestIdleCallback === 'function') {
    const handle = window.requestIdleCallback(cb, { timeout: 2000 });
    return {
      cancel() {
        try { window.cancelIdleCallback(handle); } catch {}
      },
    };
  }
  const handle = setTimeout(cb, 250);
  return { cancel() { clearTimeout(handle); } };
}

// Best-effort POST that persists peaks decoded from a legacy voice note back
// to the server so future thread opens can render the bubble instantly from
// stored data instead of repeating the warm-up decode. Safe to call from
// every client that decodes — the server only writes when peaks are still
// NULL, so the last-write-wins race is harmless. Failures are swallowed:
// the bubble already shows the freshly-decoded peaks locally either way.
function backfillPeaksToServer(messageId, peaks) {
  if (!messageId || !Array.isArray(peaks) || peaks.length === 0) return;
  if (typeof fetch !== 'function') return;
  try {
    fetch('/api/messages/peaks', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ messageId, peaks }),
    }).catch(() => {});
  } catch {}
}

// Custom waveform-style scrubber shared by the recorded-voice preview row
// and the sent/received chat bubbles. We decode the source audio in an
// AudioContext to extract per-bar peaks, then render the same kind of
// vertical bars used by the live recording level meter so the entire
// voice-note experience (record → preview → send → bubble) stays
// visually consistent.
//
// `variant` controls the colour treatment:
//   - 'preview'  — composer preview row (gray time, blue accents, glow)
//   - 'mine'     — outgoing chat bubble (white-on-blue)
//   - 'theirs'   — incoming chat bubble (blue-on-dark)
//
// For the preview row callers pass an in-memory `blob` so we can decode
// without an extra round-trip. Chat bubbles only have a URL, in which
// case we fetch + decode it once on mount. If decoding fails (unsupported
// codec, fetch error, no AudioContext, etc.) we fall back to a flat
// baseline so older voice notes still render and play.
//
// `trimStartMs` / `trimEndMs` (preview only) define the playable window
// inside the recorded take. When `onTrimChange` is supplied, two yellow
// handles appear over the bars so the user can shave silence off the head
// or tail without re-recording. Playback is clamped to the window, and the
// parent component re-encodes that same window into the blob it actually
// sends. Chat bubbles never pass `onTrimChange`, so the handles + trim UI
// are inert and the bubble waveform plays end-to-end as before.
function VoiceWaveform({
  blob,
  url,
  durationMs,
  variant = 'preview',
  trimStartMs = 0,
  trimEndMs,
  onTrimChange,
  // Pre-computed peaks captured at send time. When provided we render
  // them straight away and skip the on-demand fetch + decode entirely,
  // which is the fast path for chat bubbles whose voice notes were sent
  // after the waveform-persistence rollout.
  peaks: storedPeaks,
  // Called once with the freshly decoded peaks during the live preview
  // so the parent can ship them along with the audio when the user taps
  // Send. Optional — bubbles never pass this.
  onPeaks,
  // Server-side message id — used to backfill freshly decoded peaks for
  // legacy voice notes (sent before the persistence rollout) so subsequent
  // thread opens render those bubbles instantly too. Composer previews
  // and the in-flight optimistic insert don't have one yet, which is fine:
  // the backfill helper short-circuits when messageId is missing.
  messageId,
}) {
  const audioRef = useRef(null);
  const trackRef = useRef(null);
  // Seed `peaks` synchronously during the initial render so bubbles paint
  // their bars on the very first frame instead of briefly showing the flat
  // baseline before the decode `useEffect` runs. Two fast paths:
  //   1. `storedPeaks` — sent alongside the message after the waveform-
  //      persistence rollout. Authoritative, so it wins.
  //   2. URL-keyed LRU cache — populated by previous decodes within this
  //      session (legacy voice notes without stored peaks).
  // Composer-blob previews don't have a stable URL key, so they keep
  // starting from null and decode fresh on mount as before.
  const [peaks, setPeaks] = useState(() => {
    if (Array.isArray(storedPeaks) && storedPeaks.length > 0) return storedPeaks;
    if (!blob && url) return getCachedWaveformPeaks(url);
    return null;
  });
  const [playing, setPlaying] = useState(false);
  const [currentMs, setCurrentMs] = useState(0);
  const totalMs = Number.isFinite(durationMs) && durationMs > 0 ? durationMs : 0;
  const startMs = Math.max(0, Math.min(totalMs, Number.isFinite(trimStartMs) ? trimStartMs : 0));
  const endMs = Math.max(
    startMs,
    Math.min(totalMs, Number.isFinite(trimEndMs) ? trimEndMs : totalMs),
  );
  const trimmedMs = Math.max(0, endMs - startMs);
  const trimmable = totalMs > 0 && typeof onTrimChange === 'function';

  // Decode the source audio into a small array of normalized amplitudes that
  // we use to size each bar. We prefer the in-memory blob (composer preview)
  // and otherwise fetch the URL (sent/received bubbles). If decoding fails
  // (unsupported codec on Safari, fetch error for a remote URL, etc.) we
  // fall back to a flat baseline so the UI still renders something.
  //
  // Skipped entirely when the parent already handed us a `peaks` array —
  // chat bubbles for messages sent after the waveform-persistence rollout
  // come with their peaks pre-computed, which is the whole point of
  // storing them server-side.
  //
  // For URL-backed bubbles that *don't* have stored peaks (legacy voice
  // notes from before the persistence rollout) we consult an in-memory
  // LRU cache first so the same attachment doesn't re-fetch + re-decode
  // every time the bubble re-mounts (e.g. while scrolling a long thread).
  // Composer-blob previews are skipped intentionally — they're one-off
  // recordings whose bytes change on each take, so caching by blob
  // identity wouldn't help.
  useEffect(() => {
    if (Array.isArray(storedPeaks) && storedPeaks.length > 0) {
      setPeaks(storedPeaks);
      return undefined;
    }
    if (!blob && !url) { setPeaks(null); return undefined; }
    if (!blob && url) {
      const cached = getCachedWaveformPeaks(url);
      if (cached) {
        setPeaks(cached);
        return undefined;
      }
    }
    let cancelled = false;
    let ctx = null;
    const runDecode = async () => {
      try {
        const Ctx = typeof window !== 'undefined'
          ? (window.AudioContext || window.webkitAudioContext)
          : null;
        if (!Ctx) { if (!cancelled) setPeaks(new Array(WAVEFORM_BAR_COUNT).fill(0.4)); return; }
        let arrBuf;
        if (blob) {
          arrBuf = await blob.arrayBuffer();
        } else {
          const res = await fetch(url, { credentials: 'include' });
          if (!res.ok) throw new Error('fetch-failed');
          arrBuf = await res.arrayBuffer();
        }
        if (cancelled) return;
        ctx = new Ctx();
        const decoded = await ctx.decodeAudioData(arrBuf.slice(0));
        if (cancelled) return;
        const norm = extractPeaksFromAudioBuffer(decoded);
        // Cache successful URL decodes even if this instance was cancelled
        // mid-flight — the bytes are deterministic, and the next mount of
        // the same bubble can then pick up the result instantly. Fallback
        // baselines (the catch branch) and composer blobs are deliberately
        // skipped: failures shouldn't poison retries, and composer blobs
        // aren't stable across re-records.
        if (!blob && url) setCachedWaveformPeaks(url, norm);
        if (!cancelled) {
          setPeaks(norm);
          if (typeof onPeaks === 'function') {
            try { onPeaks(norm); } catch {}
          }
          // Persist the decoded peaks back to the server so subsequent thread
          // opens render this bubble from stored data (the fast path) instead
          // of repeating the warm-up fetch+decode. Only meaningful for legacy
          // URL-backed bubbles with a known message id; the server itself
          // refuses to overwrite a row that already has peaks.
          if (!blob && url && messageId) {
            backfillPeaksToServer(messageId, norm);
          }
        }
      } catch {
        if (!cancelled) setPeaks(new Array(WAVEFORM_BAR_COUNT).fill(0.4));
      } finally {
        if (ctx) { try { await ctx.close(); } catch {} }
      }
    };
    // Composer blob previews need to decode immediately so the user sees the
    // waveform of the take they just recorded. URL-only chat bubbles (legacy
    // voice notes that don't have stored peaks yet) defer their decode to
    // browser idle time so opening a thread doesn't kick off N parallel audio
    // downloads up-front — the bubble shows a flat baseline until the warm-up
    // completes, which matches the "low-priority background warm-up" allowance
    // in the spec for legacy attachments.
    let idle = null;
    if (blob) {
      runDecode();
    } else {
      idle = scheduleIdle(() => { if (!cancelled) runDecode(); });
    }
    return () => {
      cancelled = true;
      if (idle) idle.cancel();
    };
  }, [blob, url, messageId]);

  // Reset transport state when the source changes (e.g. after re-record,
  // or when scrolling between bubbles backed by different attachments).
  // Snap the playhead to the start of the trim window so the play button
  // doesn't appear to skip past silence the user didn't intend to keep.
  useEffect(() => {
    setPlaying(false);
    setCurrentMs(startMs);
    const a = audioRef.current;
    if (a) {
      try { a.currentTime = startMs / 1000; } catch {}
    }
    // Intentionally only react to a new blob — startMs is captured here so a
    // re-record resets the playhead, but tweaks to the handles below handle
    // their own re-clamping without rewinding mid-playback.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  // If this player unmounts (e.g. the thread closes or the bubble scrolls out
  // of a virtualized list in the future) make sure we don't leave a dangling
  // reference in the shared registry.
  useEffect(() => () => {
    const a = audioRef.current;
    if (a) releaseVoicePlayback(a);
  }, []);

  // If the user drags a handle past the current playhead, snap the playhead
  // back into the new window so we don't keep playing audio that's now
  // outside the trim range.
  useEffect(() => {
    if (totalMs <= 0) return;
    setCurrentMs((prev) => {
      if (prev < startMs) return startMs;
      if (prev > endMs) return endMs;
      return prev;
    });
    const a = audioRef.current;
    if (!a) return;
    const cur = a.currentTime * 1000;
    if (cur < startMs - 5) {
      try { a.currentTime = startMs / 1000; } catch {}
    } else if (cur > endMs) {
      try { a.pause(); } catch {}
      try { a.currentTime = startMs / 1000; } catch {}
    }
  }, [startMs, endMs, totalMs]);

  const togglePlay = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      try { a.pause(); } catch {}
    } else {
      // Pause any other voice clip *before* we start ours so the two
      // never overlap, even briefly. The native `play` event fires
      // asynchronously (sometimes after a buffering delay), so relying
      // on the audio element's `onPlay` handler alone leaves a window
      // where both clips are audible. Claiming the registry slot here
      // synchronously pauses whichever clip held it previously, and
      // that pause fires the other bubble's native `pause` event so
      // its UI flips back to the play state without any extra wiring.
      claimVoicePlayback(a);
      // If we're outside the trim window or sitting at its end, rewind to
      // the start of the trim so play picks up from the trimmed beginning.
      if (trimmedMs > 0 && (currentMs < startMs || currentMs >= endMs - 50)) {
        try { a.currentTime = startMs / 1000; } catch {}
        setCurrentMs(startMs);
      }
      const p = a.play();
      if (p && typeof p.catch === 'function') {
        p.catch((err) => {
          // Log play() rejections so silent preview/bubble playback failures
          // (autoplay block, NotSupportedError on a malformed blob, missing
          // src, etc.) leave a breadcrumb in the console rather than just
          // bouncing the play button back to the idle state.
          try {
            console.error('[voice-note] audio.play() failed:', {
              variant,
              hasSrc: !!a.src,
              readyState: a.readyState,
              networkState: a.networkState,
              error: err?.name || err?.message || String(err),
            });
          } catch (_e) {}
        });
      }
    }
  };

  const seekFromClientX = (clientX) => {
    const a = audioRef.current;
    const track = trackRef.current;
    if (!a || !track || totalMs <= 0) return;
    const rect = track.getBoundingClientRect();
    if (rect.width <= 0) return;
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    let ms = ratio * totalMs;
    // Clamp scrub to the trim window so clicking in the dimmed (trimmed-out)
    // region snaps to the nearest edge instead of seeking outside it.
    ms = Math.max(startMs, Math.min(endMs, ms));
    try { a.currentTime = ms / 1000; } catch {}
    setCurrentMs(ms);
  };

  // Drag-to-scrub via Pointer Events: a single press seeks (just like the
  // previous click handler), and any subsequent movement before release keeps
  // updating the playback position in real time. setPointerCapture means we
  // keep getting events even when the finger/cursor leaves the bar.
  const draggingRef = useRef(false);

  const handlePointerDown = (e) => {
    if (totalMs <= 0) return;
    // Only react to the primary mouse button (or touch/pen presses, which
    // report button === 0 too). Skip right-clicks etc.
    if (e.button !== undefined && e.button !== 0) return;
    const track = trackRef.current;
    if (track && typeof track.setPointerCapture === 'function') {
      try { track.setPointerCapture(e.pointerId); } catch {}
    }
    draggingRef.current = true;
    seekFromClientX(e.clientX);
  };

  const handlePointerMove = (e) => {
    if (!draggingRef.current) return;
    // Prevent the browser from interpreting the gesture as a scroll/swipe
    // while we're actively scrubbing.
    if (e.cancelable) e.preventDefault();
    seekFromClientX(e.clientX);
  };

  const endDrag = (e) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    const track = trackRef.current;
    if (track && typeof track.releasePointerCapture === 'function') {
      try { track.releasePointerCapture(e.pointerId); } catch {}
    }
    seekFromClientX(e.clientX);
  };

  const handleTrackKeyDown = (e) => {
    const a = audioRef.current;
    if (!a || totalMs <= 0) return;
    const step = Math.max(250, trimmedMs * 0.05);
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      const next = Math.max(startMs, currentMs - step);
      try { a.currentTime = next / 1000; } catch {}
      setCurrentMs(next);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      const next = Math.min(endMs, currentMs + step);
      try { a.currentTime = next / 1000; } catch {}
      setCurrentMs(next);
    } else if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      togglePlay();
    }
  };

  // Trim-handle drag — separate pointer-capture lifecycle from the scrub
  // drag above so the handle wins when both could fire (the handle is
  // rendered on top of the bars and stops propagation).
  const handleDragModeRef = useRef(null);

  const trimMsFromClientX = (clientX) => {
    const track = trackRef.current;
    if (!track) return null;
    const rect = track.getBoundingClientRect();
    if (rect.width <= 0) return null;
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    return ratio * totalMs;
  };

  const applyHandleDrag = (clientX) => {
    if (!trimmable) return;
    const ms = trimMsFromClientX(clientX);
    if (ms == null) return;
    if (handleDragModeRef.current === 'start') {
      const next = Math.max(0, Math.min(ms, endMs - MIN_TRIM_MS));
      onTrimChange(next, endMs);
    } else if (handleDragModeRef.current === 'end') {
      const next = Math.min(totalMs, Math.max(ms, startMs + MIN_TRIM_MS));
      onTrimChange(startMs, next);
    }
  };

  const startHandleDrag = (mode) => (e) => {
    if (!trimmable) return;
    if (e.button !== undefined && e.button !== 0) return;
    // Don't let the underlying scrub track steal this gesture — the trim
    // handles render on top of the bars and own this pointer interaction.
    e.stopPropagation();
    if (e.cancelable) e.preventDefault();
    handleDragModeRef.current = mode;
    const target = e.currentTarget;
    if (target && typeof target.setPointerCapture === 'function') {
      try { target.setPointerCapture(e.pointerId); } catch {}
    }
  };

  const moveHandleDrag = (e) => {
    if (!handleDragModeRef.current) return;
    if (e.cancelable) e.preventDefault();
    applyHandleDrag(e.clientX);
  };

  const endHandleDrag = (e) => {
    if (!handleDragModeRef.current) return;
    applyHandleDrag(e.clientX);
    const target = e.currentTarget;
    if (target && typeof target.releasePointerCapture === 'function') {
      try { target.releasePointerCapture(e.pointerId); } catch {}
    }
    handleDragModeRef.current = null;
  };

  const nudgeHandle = (mode, deltaMs) => {
    if (!trimmable) return;
    if (mode === 'start') {
      const next = Math.max(0, Math.min(startMs + deltaMs, endMs - MIN_TRIM_MS));
      onTrimChange(next, endMs);
    } else {
      const next = Math.min(totalMs, Math.max(endMs + deltaMs, startMs + MIN_TRIM_MS));
      onTrimChange(startMs, next);
    }
  };

  const handleHandleKeyDown = (mode) => (e) => {
    if (!trimmable) return;
    const step = Math.max(100, totalMs * 0.02);
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      e.stopPropagation();
      nudgeHandle(mode, -step);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      e.stopPropagation();
      nudgeHandle(mode, step);
    } else if (e.key === 'Home') {
      e.preventDefault();
      e.stopPropagation();
      if (mode === 'start') onTrimChange(0, endMs);
      else onTrimChange(startMs, Math.max(startMs + MIN_TRIM_MS, totalMs));
    } else if (e.key === 'End') {
      e.preventDefault();
      e.stopPropagation();
      if (mode === 'end') onTrimChange(startMs, totalMs);
      else onTrimChange(Math.max(0, endMs - MIN_TRIM_MS), endMs);
    }
  };

  const startPct = totalMs > 0 ? (startMs / totalMs) * 100 : 0;
  const endPct = totalMs > 0 ? (endMs / totalMs) * 100 : 100;
  // Progress is only meaningful inside the trim window — keep using the
  // overall ratio (so the playhead aligns with the bars) but the painted
  // "played" state below is gated to bars that fall inside the window.
  const progress = totalMs > 0 ? Math.min(1, Math.max(0, currentMs / totalMs)) : 0;
  const playedInTrim = Math.max(0, Math.min(trimmedMs, currentMs - startMs));
  // Once playback has started (or the user has scrubbed off the start) show
  // the elapsed time alongside the total length of the *trimmed* take —
  // pairs with the playhead line so users can see exactly how far in they
  // are. Idle bubbles still just show the total length so the row stays
  // compact before any interaction.
  const inPlayback = playing || playedInTrim > 0;
  const elapsedLabel = formatDuration(playedInTrim);
  const totalLabel = formatDuration(trimmedMs);
  const timeLabel = inPlayback ? `${elapsedLabel} / ${totalLabel}` : totalLabel;
  const bars = peaks ?? new Array(WAVEFORM_BAR_COUNT).fill(0.25);
  const trimmed = trimmedMs < totalMs - 1;

  const isPreview = variant === 'preview';
  const isMine = variant === 'mine';
  // White-on-blue treatment for outgoing bubbles (which sit on a solid
  // bg-blue-500 background) and blue-on-dark for the preview row and
  // incoming bubbles (which sit on the dark composer / dark thread bg).
  const palette = isMine
    ? {
        playBg: 'rgba(255,255,255,0.22)',
        playIcon: '#ffffff',
        playShadow: 'none',
        played: '#ffffff',
        unplayed: 'rgba(255,255,255,0.4)',
        time: 'rgba(255,255,255,0.85)',
      }
    : {
        playBg: '#3b82f6',
        playIcon: '#ffffff',
        playShadow: isPreview ? '0 0 10px rgba(59,130,246,0.5)' : 'none',
        played: '#3b82f6',
        unplayed: 'rgba(96,165,250,0.35)',
        time: '#9ca3af',
      };

  const playLabel = playing
    ? (isPreview ? 'Pause voice preview' : 'Pause voice message')
    : (isPreview ? 'Play voice preview' : 'Play voice message');
  const sliderLabel = isPreview ? 'Voice preview scrubber' : 'Voice message scrubber';

  return (
    <div className={`flex items-center gap-2 ${isPreview ? 'min-w-0 flex-1' : ''}`}>
      <audio
        ref={audioRef}
        src={url}
        // Chat bubbles use preload="none" so opening a thread doesn't kick
        // off a network request for every voice note up-front (waveform
        // bars come from persisted peaks; bytes are loaded lazily when
        // togglePlay calls .play()).
        //
        // The composer preview is the opposite: the blob is already in
        // memory and the user is about to tap play, so we let the element
        // load metadata immediately. Without this, some browsers (and
        // playwright) don't reliably resolve the blob URL on the first
        // .play() and the preview row plays silently — the same regression
        // that surfaced as "tapping play on the just-recorded preview
        // produces no audio" in this task.
        preload={isPreview ? 'metadata' : 'none'}
        onPlay={(e) => { claimVoicePlayback(e.currentTarget); setPlaying(true); }}
        onPause={(e) => { releaseVoicePlayback(e.currentTarget); setPlaying(false); }}
        onEnded={(e) => { releaseVoicePlayback(e.currentTarget); setPlaying(false); setCurrentMs(endMs); }}
        onTimeUpdate={(e) => {
          const ms = e.currentTarget.currentTime * 1000;
          // Stop playback at the trim end so the preview only plays the
          // window the user will actually send. Chat bubbles use the full
          // take, so endMs === totalMs and this branch is never taken.
          if (endMs > 0 && endMs < totalMs && ms >= endMs) {
            try { e.currentTarget.pause(); } catch {}
            try { e.currentTarget.currentTime = startMs / 1000; } catch {}
            setCurrentMs(startMs);
          } else if (startMs > 0 && ms < startMs) {
            try { e.currentTarget.currentTime = startMs / 1000; } catch {}
            setCurrentMs(startMs);
          } else {
            setCurrentMs(ms);
          }
        }}
        style={{ display: 'none' }}
      />
      <button
        type="button"
        onClick={togglePlay}
        aria-label={playLabel}
        className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center"
        style={{
          backgroundColor: palette.playBg,
          color: palette.playIcon,
          boxShadow: palette.playShadow,
        }}
      >
        {playing ? (
          <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true">
            <rect x="2.5" y="2" width="2.5" height="8" rx="0.5" />
            <rect x="7" y="2" width="2.5" height="8" rx="0.5" />
          </svg>
        ) : (
          <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true">
            <path d="M3 1.6 L10 6 L3 10.4 Z" />
          </svg>
        )}
      </button>
      <div
        ref={trackRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onKeyDown={handleTrackKeyDown}
        role="slider"
        aria-label={sliderLabel}
        aria-valuemin={0}
        aria-valuemax={Math.max(1, Math.round(totalMs))}
        aria-valuenow={Math.round(currentMs)}
        tabIndex={0}
        // `relative` is required so both the trim handles (preview only)
        // and the playhead line can be absolutely positioned over the bars.
        // `overflow-hidden` is intentionally skipped in preview mode so the
        // trim handles' slight vertical overflow (top/bottom: -2) isn't
        // clipped; outgoing/incoming bubbles still clip to a fixed width.
        className={`relative flex items-center gap-[2px] h-8 cursor-pointer outline-none ${isPreview ? 'min-w-0 flex-1' : 'overflow-hidden w-[150px] max-w-full'}`}
        // touch-action: none keeps the browser from stealing horizontal drags
        // for page scroll/back-swipe while the user is scrubbing.
        style={{ touchAction: 'none' }}
      >
        {bars.map((p, i) => {
          const h = Math.max(2, Math.round(p * 24));
          const barCenter = (i + 0.5) / WAVEFORM_BAR_COUNT;
          const inTrim = totalMs <= 0
            || (barCenter * totalMs >= startMs && barCenter * totalMs <= endMs);
          const played = inTrim && barCenter <= progress;
          let bg;
          if (!inTrim) bg = 'rgba(96,165,250,0.12)';
          else if (played) bg = '#3b82f6';
          else bg = 'rgba(96,165,250,0.45)';
          return (
            <span
              key={i}
              className="block rounded-sm"
              style={{
                width: 2,
                height: `${h}px`,
                backgroundColor: !inTrim
                  ? (isMine ? 'rgba(255,255,255,0.18)' : 'rgba(96,165,250,0.15)')
                  : (played ? palette.played : palette.unplayed),
                transition: 'background-color 60ms linear',
              }}
            />
          );
        })}
        {trimmable && (
          <>
            {/* Translucent overlay highlighting the kept (in-trim) region so
                the active window reads at a glance even before any drag. */}
            <span
              aria-hidden="true"
              style={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: `${startPct}%`,
                width: `${Math.max(0, endPct - startPct)}%`,
                pointerEvents: 'none',
                background: 'rgba(59,130,246,0.08)',
                borderTop: '1px solid rgba(59,130,246,0.35)',
                borderBottom: '1px solid rgba(59,130,246,0.35)',
              }}
            />
            <button
              type="button"
              role="slider"
              aria-label="Trim start"
              aria-valuemin={0}
              aria-valuemax={Math.max(0, Math.round(endMs - MIN_TRIM_MS))}
              aria-valuenow={Math.round(startMs)}
              onPointerDown={startHandleDrag('start')}
              onPointerMove={moveHandleDrag}
              onPointerUp={endHandleDrag}
              onPointerCancel={endHandleDrag}
              onKeyDown={handleHandleKeyDown('start')}
              title="Drag to trim the start"
              style={{
                position: 'absolute',
                top: -2,
                bottom: -2,
                left: `${startPct}%`,
                transform: 'translateX(-50%)',
                width: 18,
                padding: 0,
                margin: 0,
                background: 'transparent',
                border: 'none',
                cursor: 'ew-resize',
                touchAction: 'none',
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  display: 'block',
                  width: 4,
                  height: '100%',
                  margin: '0 auto',
                  background: '#fbbf24',
                  borderRadius: 2,
                  boxShadow: '0 0 6px rgba(251,191,36,0.7)',
                }}
              />
            </button>
            <button
              type="button"
              role="slider"
              aria-label="Trim end"
              aria-valuemin={Math.round(startMs + MIN_TRIM_MS)}
              aria-valuemax={Math.max(1, Math.round(totalMs))}
              aria-valuenow={Math.round(endMs)}
              onPointerDown={startHandleDrag('end')}
              onPointerMove={moveHandleDrag}
              onPointerUp={endHandleDrag}
              onPointerCancel={endHandleDrag}
              onKeyDown={handleHandleKeyDown('end')}
              title="Drag to trim the end"
              style={{
                position: 'absolute',
                top: -2,
                bottom: -2,
                left: `${endPct}%`,
                transform: 'translateX(-50%)',
                width: 18,
                padding: 0,
                margin: 0,
                background: 'transparent',
                border: 'none',
                cursor: 'ew-resize',
                touchAction: 'none',
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  display: 'block',
                  width: 4,
                  height: '100%',
                  margin: '0 auto',
                  background: '#fbbf24',
                  borderRadius: 2,
                  boxShadow: '0 0 6px rgba(251,191,36,0.7)',
                }}
              />
            </button>
          </>
        )}
        {/* Vertical playhead line: easier to follow than recoloring alone,
            especially across short/quiet bars. Hidden in the idle state so
            the waveform still looks clean before the user has hit play.
            Rendered after the trim handles so it sits visually on top. */}
        {(playing || currentMs > 0) && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute top-1/2"
            style={{
              left: `${progress * 100}%`,
              width: 2,
              height: 28,
              marginLeft: -1,
              transform: 'translateY(-50%)',
              backgroundColor: palette.played,
              borderRadius: 1,
              // Smooth out the gaps between `timeupdate` ticks while playing,
              // but skip the transition while the user is actively scrubbing
              // so the line tracks their finger/cursor 1:1.
              transition: playing && !draggingRef.current
                ? 'left 90ms linear'
                : 'none',
            }}
          />
        )}
      </div>
      <span
        className="text-[10px] tabular-nums flex-shrink-0"
        style={{ color: trimmable && trimmed ? '#fbbf24' : palette.time }}
        title={trimmable && trimmed ? 'Trimmed length' : undefined}
      >
        {timeLabel}
      </span>
    </div>
  );
}

// Thin wrapper used by the chat thread so the call site stays expressive.
// Voice notes sent after the waveform-persistence rollout carry their
// pre-computed peaks on the message row, so we hand those straight to
// VoiceWaveform and skip the bubble's mount-time fetch+decode entirely.
// Older messages have no peaks stored: VoiceWaveform falls back to the
// on-demand decode path (and to a flat baseline if that decode fails).
// SharedItemBubble — renders a Messenger preview-card bubble for a battle
// or post shared from the social feed. The payload lives in `m.content`
// as JSON (`{v,type,id,note,snapshot}`); we parse defensively and fall
// back to plain text if anything is off. Tapping the card deep-links to
// the spectator page (battle) or the social feed (post).
function SharedItemBubble({ raw, mine, onNavigate, senderId, senderUsername }) {
  let payload = null;
  try { payload = JSON.parse(raw); } catch { payload = null; }
  if (!payload || !['battle', 'post', 'result'].includes(payload.type)) {
    return <span>{raw}</span>;
  }
  const { type } = payload;
  const snap = payload.snapshot || {};
  // Only carry the share-attribution query params when the recipient is
  // the one tapping the bubble — opening your own outgoing share doesn't
  // need a "Shared by you" pill on the destination page.
  const buildShareSuffix = (joiner) => {
    if (mine || !senderId) return '';
    const parts = [`shared=${encodeURIComponent(senderId)}`];
    if (senderUsername) parts.push(`sharedBy=${encodeURIComponent(senderUsername)}`);
    return `${joiner}${parts.join('&')}`;
  };
  const handleClick = () => {
    if (!payload.id) return;
    if (type === 'battle') onNavigate?.(`/battle?battle=${encodeURIComponent(payload.id)}${buildShareSuffix('&')}`);
    else if (type === 'result') onNavigate?.(`/battle/replay/${payload.id}${buildShareSuffix('?')}`);
    // Post deep-link: SocialFeedPage reads ?post= and scrolls + opens
    // the post's comment thread so the recipient lands on the original.
    else if (type === 'post') onNavigate?.(`/battle?post=${encodeURIComponent(payload.id)}${buildShareSuffix('&')}`);
  };
  const headerBg = type === 'battle'
    ? 'linear-gradient(135deg,#2563eb,#f97316)'
    : (type === 'result' ? 'linear-gradient(135deg,#10b981,#2563eb)' : 'linear-gradient(135deg,#06b6d4,#2563eb)');
  const headerLabel = type === 'battle' ? 'Live Battle' : (type === 'result' ? 'Battle Result' : 'Post');
  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        onClick={handleClick}
        className="rounded-xl overflow-hidden text-left transition-transform active:scale-[0.98]"
        style={{
          background: mine ? 'rgba(0,0,0,0.25)' : 'rgba(255,255,255,0.06)',
          border: '1px solid rgba(255,255,255,0.12)',
          width: 240,
        }}
      >
        <div className="px-3 py-2 flex items-center gap-2" style={{ background: headerBg }}>
          <span className="text-[10px] font-black uppercase tracking-wider text-white">{headerLabel}</span>
        </div>
        <div className="px-3 py-2.5">
          {type === 'battle' && (
            <>
              <div className="text-[13px] font-bold text-white truncate">
                {(snap.user1?.username || 'P1')} vs {(snap.user2?.username || 'P2')}
              </div>
              <div className="text-[11px] text-white/70 mt-0.5">
                {snap.durationType ? `${snap.durationType} · ` : ''}Tap to view
              </div>
            </>
          )}
          {type === 'result' && (
            <>
              <div className="text-[13px] font-bold text-white truncate">
                <span className="text-emerald-300">{snap.winner?.username || 'Winner'}</span>
                <span className="text-white/60"> beat </span>
                {snap.loser?.username || 'Opponent'}
              </div>
              <div className="text-[11px] text-white/70 mt-0.5">Tap to watch the replay</div>
            </>
          )}
          {type === 'post' && (
            <>
              <div className="text-[11px] text-white/70 mb-1">@{snap.author?.username || 'someone'}</div>
              <div className="text-[13px] text-white leading-snug" style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {snap.body || 'Shared post'}
              </div>
            </>
          )}
        </div>
      </button>
      {payload.note && (
        <div className="text-[13px] leading-snug break-words">{payload.note}</div>
      )}
    </div>
  );
}

function VoiceBubble({ url, durationMs, peaks, mine, messageId }) {
  return (
    <VoiceWaveform
      url={url}
      durationMs={durationMs}
      peaks={peaks}
      messageId={messageId}
      variant={mine ? 'mine' : 'theirs'}
    />
  );
}

/**
 * Single click target for the friend's identity row in the conversation
 * header. Wraps the avatar, username, and active-status row in one
 * <Link> so the entire block (including the gap between avatar and
 * name) navigates to /profile/{friend.id}. Defensive: if `friend.id`
 * is missing (still loading) we render the same visual without the
 * link wrapper so the layout doesn't shift when the id arrives.
 */
function ProfileHeaderLink({ friend, textPrimary, textSecondary }) {
  const prefetch = useProfilePrefetchHandlers(friend);
  const inner = (
    <>
      <UserAvatar
        user={friend}
        isOnline={friend?.isOnline ?? isUserOnline(friend?.lastSeenAt)}
        onlineDotBorderColor={'#0a0a0a'}
      />
      <div className="flex-1 min-w-0">
        <div
          className="text-sm font-semibold truncate"
          style={{ color: textPrimary }}
        >
          {friend?.username || 'Player'}
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <ActiveStatus
            isOnline={friend?.isOnline}
            lastSeenAt={friend?.lastSeenAt}
            size="xs"
          />
          {(friend?.battleWins != null || friend?.battleLosses != null) && (
            <span className="text-[10px]" style={{ color: textSecondary }}>
              · {friend?.battleWins || 0}W - {friend?.battleLosses || 0}L
            </span>
          )}
        </div>
      </div>
    </>
  );
  if (!friend?.id) {
    return <div className="flex items-center gap-3 flex-1 min-w-0">{inner}</div>;
  }
  return (
    <Link
      href={`/profile/${friend.id}`}
      aria-label={`View ${friend.username || 'profile'}`}
      className="flex items-center gap-3 flex-1 min-w-0 -mx-1 px-1 py-1 rounded-lg hover:bg-white/5 transition-colors"
      {...prefetch}
    >
      {inner}
    </Link>
  );
}

export function ConversationThread({ friend, ctx, myId, onStartBattle, onBack }) {
  const { hasActiveMatchup } = useMatchup();
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const router = useRouter();
  const [thread, setThread] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [notFriends, setNotFriends] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  // Instagram-style @mention autocomplete for the composer. `mentionBox` is
  // either null or { results, activeIndex } — the dropdown of users matching
  // the handle the caret is currently typing. The abort ref cancels in-flight
  // searches so a fast typist's earlier query can't clobber a newer one.
  const [mentionBox, setMentionBox] = useState(null);
  const mentionAbortRef = useRef(null);
  // Track which message IDs are mid-delete so we can disable the
  // delete affordance and gray the bubble while the request is in
  // flight (prevents double-deletes from impatient double-taps).
  const [deletingIds, setDeletingIds] = useState(() => new Set());

  // Unsend / delete an outgoing message. Optimistically removes the
  // bubble first, then DELETEs on the server. On failure we restore
  // the bubble and surface a lightweight inline error. The server
  // broadcasts `notification:message_deleted` so the recipient's
  // open thread (and our other tabs) drop the bubble too.
  const handleDeleteMessage = useCallback(async (messageId) => {
    if (!messageId) return;
    if (typeof window !== 'undefined' && typeof window.confirm === 'function') {
      const ok = window.confirm('Delete this message for everyone? This cannot be undone.');
      if (!ok) return;
    }
    let removed = null;
    setThread((prev) => {
      const idx = prev.findIndex((x) => x.id === messageId);
      if (idx === -1) return prev;
      removed = { msg: prev[idx], idx };
      const next = prev.slice();
      next.splice(idx, 1);
      return next;
    });
    setDeletingIds((prev) => {
      const next = new Set(prev);
      next.add(messageId);
      return next;
    });
    try {
      const res = await fetch(`/api/messages/${encodeURIComponent(messageId)}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok && res.status !== 404) {
        throw new Error('delete_failed');
      }
    } catch (_e) {
      if (removed) {
        setThread((prev) => {
          if (prev.some((x) => x.id === messageId)) return prev;
          const next = prev.slice();
          const insertAt = Math.min(removed.idx, next.length);
          next.splice(insertAt, 0, removed.msg);
          return next;
        });
      }
      setSendError('Could not delete message. Try again.');
    } finally {
      setDeletingIds((prev) => {
        if (!prev.has(messageId)) return prev;
        const next = new Set(prev);
        next.delete(messageId);
        return next;
      });
    }
  }, []);

  // "Delete for me" — hides the bubble from this user's view only. The
  // other participant keeps seeing it (no SSE broadcast). Optimistically
  // removes locally, then persists via ?scope=me; restores on failure.
  const handleDeleteForMe = useCallback(async (messageId) => {
    if (!messageId) return;
    let removed = null;
    setThread((prev) => {
      const idx = prev.findIndex((x) => x.id === messageId);
      if (idx === -1) return prev;
      removed = { msg: prev[idx], idx };
      const next = prev.slice();
      next.splice(idx, 1);
      return next;
    });
    setDeletingIds((prev) => {
      const next = new Set(prev);
      next.add(messageId);
      return next;
    });
    try {
      const res = await fetch(`/api/messages/${encodeURIComponent(messageId)}?scope=me`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok && res.status !== 404) {
        throw new Error('delete_failed');
      }
    } catch (_e) {
      if (removed) {
        setThread((prev) => {
          if (prev.some((x) => x.id === messageId)) return prev;
          const next = prev.slice();
          const insertAt = Math.min(removed.idx, next.length);
          next.splice(insertAt, 0, removed.msg);
          return next;
        });
      }
      setSendError('Could not delete message. Try again.');
    } finally {
      setDeletingIds((prev) => {
        if (!prev.has(messageId)) return prev;
        const next = new Set(prev);
        next.delete(messageId);
        return next;
      });
    }
  }, []);

  // Drop a bubble when the server broadcasts a deletion (e.g. the
  // other party unsent a message, or this user unsent from another
  // tab). The optimistic path above already removes locally, so this
  // is a no-op for the originating tab.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handler = (e) => {
      const id = e?.detail?.messageId;
      if (!id) return;
      setThread((prev) => {
        if (!prev.some((x) => x.id === id)) return prev;
        return prev.filter((x) => x.id !== id);
      });
    };
    window.addEventListener('piks:message:deleted', handler);
    return () => window.removeEventListener('piks:message:deleted', handler);
  }, []);

  const [recording, setRecording] = useState(false);
  const [paused, setPaused] = useState(false);
  // Set when the most recent pause was *not* a manual Pause tap, so we can
  // show a one-line "we paused for you" hint and explain why. `null` means
  // either we're not paused or the user paused themselves (no hint needed).
  // 'tab'    — auto-paused because the tab was hidden / window blurred.
  // 'system' — paused by the browser/OS (mic taken by another app, device
  //            locked, headset disconnected, etc.). Detected via the
  //            MediaRecorder `pause` event firing without us asking, or via
  //            the audio track's `mute`/`ended` events.
  const [pauseReason, setPauseReason] = useState(null);
  const [pauseSupported, setPauseSupported] = useState(true);
  const [recordElapsed, setRecordElapsed] = useState(0);
  const [voiceError, setVoiceError] = useState(null);
  const LEVEL_BAR_COUNT = 18;
  const [audioLevels, setAudioLevels] = useState(() => new Array(LEVEL_BAR_COUNT).fill(0));
  const [voicePreview, setVoicePreview] = useState(null);
  // True when, after the current preview, the user can tap "Continue" to add
  // more audio to the same take instead of starting over. On browsers with
  // pause+requestData this is backed by a still-alive (paused) MediaRecorder;
  // on Safari/iOS where those APIs aren't usable we instead spin up a fresh
  // recorder and stitch the resulting segments together on send. False only
  // when the take is finalized for good (max duration hit, send completed,
  // re-record tapped, friend switched, etc.).
  const [resumableTake, setResumableTake] = useState(false);
  // True between tapping "Done" on a multi-segment take and the
  // concatenated-WAV preview being ready, so the composer can show a brief
  // "Processing…" pill instead of flickering back to the text input. Only
  // ever flipped on the segment-stitching fallback path.
  const [processingPreview, setProcessingPreview] = useState(false);
  const recorderRef = useRef(null);
  const recordChunksRef = useRef([]);
  const recordStartRef = useRef(0);
  // Accumulated elapsed ms from previously-completed segments (i.e. the time
  // captured before the current pause/resume cycle, plus — on the segment
  // fallback — the duration of all earlier finalized segments). The visible
  // elapsed time is this plus the current segment's running time.
  const recordAccumRef = useRef(0);
  // Previously-finalized segment blobs (and their MIME) for the Safari/iOS
  // fallback. Empty on the alive-recorder path. Concatenated into the
  // playable WAV preview/upload blob whenever there are 2+ entries.
  const segmentsRef = useRef([]);
  // Set by handleStopRecording before calling rec.stop() to tell the onstop
  // handler that the user is finalizing a *segment* (and expects a preview
  // they can still Continue from), as opposed to a real end-of-take stop
  // (cancel, max-duration auto-stop, friend switch). Read-and-cleared inside
  // onstop so the state doesn't leak across recorder lifecycles.
  const stopReasonRef = useRef(null);
  const recordPausedRef = useRef(false);
  // Set to true immediately before we ourselves call rec.pause() (manual
  // tap, auto-pause-on-hidden, pause-for-preview). The MediaRecorder
  // `onpause` handler reads & clears this so it can tell our intentional
  // pauses apart from system-initiated ones (mic stolen, device locked,
  // headset unplugged, etc.) and only flag the latter as 'system'.
  const selfPauseExpectedRef = useRef(false);
  const recordTimerRef = useRef(null);
  const recordCancelledRef = useRef(false);
  const recordMimeRef = useRef('audio/webm');
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const audioRafRef = useRef(null);
  const scrollRef = useRef(null);
  const lastTypingSentRef = useRef(0);
  const lastTypingFriendRef = useRef(null);
  const inputRef = useRef(null);
  // Tracks the most recently surfaced voice-note error toast id so repeated
  // Send taps replace the previous toast instead of stacking a new red card
  // on top of an old one. Cleared on successful send, friend switch, and
  // unmount so a stale toast never lingers across conversations.
  const lastVoiceErrorToastIdRef = useRef(null);
  const isTyping = !!friend?.id && ctx.typingSenderIds?.has?.(friend.id);

  useEffect(() => {
    if (!friend?.id) return undefined;
    const key = `message:${friend.id}`;
    ctx.setSuppress?.(key, true);
    return () => ctx.setSuppress?.(key, false);
  }, [friend?.id, ctx]);

  useEffect(() => {
    if (!friend?.id) return undefined;
    let cancelled = false;
    let timer = null;

    const fetchThread = async ({ initial }) => {
      if (initial) { setLoading(true); setLoadError(null); setNotFriends(false); }
      try {
        const res = await fetch(`/api/messages?friendId=${friend.id}`, { credentials: 'include' });
        if (!res.ok) {
          if (!cancelled && initial) {
            if (res.status === 403) {
              setNotFriends(true);
            } else {
              setLoadError('Could not load messages.');
            }
          }
          return;
        }
        if (!cancelled) setNotFriends(false);
        const data = await res.json();
        if (cancelled) return;
        const next = data.messages || [];
        setThread((prev) => {
          const prevIds = new Set(prev.map((m) => m.id));
          const incomingFromFriend = next.some(
            (m) => !prevIds.has(m.id) && m.senderId === friend.id
          );
          if (incomingFromFriend) ctx.clearTyping?.(friend.id);
          return next;
        });
      } catch {
        if (!cancelled && initial) setLoadError('Could not load messages.');
      } finally {
        if (!cancelled && initial) setLoading(false);
      }
    };

    fetchThread({ initial: true });
    timer = setInterval(() => fetchThread({ initial: false }), 5000);

    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchThread({ initial: false });
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [friend?.id, reloadKey]);

  useEffect(() => {
    if (!friend?.id || typeof window === 'undefined') return undefined;
    const handler = (e) => {
      const m = e?.detail;
      if (!m || !m.id) return;
      const fromFriend = m.senderId === friend.id && (myId == null || m.receiverId === myId);
      const fromMeToFriend = myId != null && m.senderId === myId && m.receiverId === friend.id;
      if (!fromFriend && !fromMeToFriend) return;
      setThread((prev) => {
        if (prev.some((x) => String(x.id) === String(m.id))) return prev;
        return [...prev, m];
      });
      if (fromFriend) ctx.clearTyping?.(friend.id);
    };
    window.addEventListener('piks:message:new', handler);
    return () => window.removeEventListener('piks:message:new', handler);
  }, [friend?.id, myId, ctx]);

  // Scroll the *inner* container only. Never use scrollIntoView (which can
  // scroll the outer page when the chat body isn't the nearest scrollable
  // ancestor or fits within the viewport).
  useEffect(() => {
    scrollToBottom(scrollRef.current);
  }, [thread, loading]);

  useEffect(() => {
    // If we were broadcasting typing in a previous chat, send a stop ping so
    // the previous friend's open thread clears their indicator immediately
    // instead of waiting for the TTL to expire.
    const prevTypingFriend = lastTypingFriendRef.current;
    if (prevTypingFriend && prevTypingFriend !== friend?.id) {
      ctx.notifyStoppedTyping?.(prevTypingFriend);
    }
    lastTypingSentRef.current = 0;
    lastTypingFriendRef.current = null;
    setReply('');
    setSendError(null);
    // Only auto-focus on devices with a real keyboard (desktop / mouse).
    // On touch devices, focusing the input would pop the iOS on-screen
    // keyboard the moment a thread opens — taking up half the screen and
    // making it harder for the user to back out of Messenger. The
    // composer is still focusable by tapping it.
    if (typeof window !== 'undefined') {
      let isTouch = false;
      try {
        if (typeof window.matchMedia === 'function') {
          isTouch = window.matchMedia('(hover: none) and (pointer: coarse)').matches;
        }
      } catch (_e) {
        isTouch = false;
      }
      if (!isTouch) inputRef.current?.focus();
    }
  }, [friend?.id]);

  // On unmount (navigating away from the messenger entirely, closing the
  // panel, etc.) make sure we tell the friend we stopped typing so their
  // indicator doesn't linger for the full TTL. ctxRef avoids re-running
  // the cleanup on every context value identity change.
  const ctxRef = useRef(ctx);
  useEffect(() => { ctxRef.current = ctx; }, [ctx]);
  useEffect(() => {
    return () => {
      const f = lastTypingFriendRef.current;
      if (f) {
        ctxRef.current?.notifyStoppedTyping?.(f);
        lastTypingFriendRef.current = null;
      }
    };
  }, []);

  const handleReplyChange = (e) => {
    const v = e.target.value;
    const prev = reply;
    setReply(v);
    if (sendError) setSendError(null);
    if (voiceError) setVoiceError(null);
    // Drive the @mention picker off the caret position. Search needs ≥2 chars
    // (matches the API's minimum); anything shorter just hides the dropdown.
    const det = detectMention(v, e.target.selectionStart ?? v.length);
    // Always cancel any in-flight search first so a slower earlier response
    // can't repopulate the dropdown after the mention is gone/changed.
    if (mentionAbortRef.current) { try { mentionAbortRef.current.abort(); } catch {} mentionAbortRef.current = null; }
    if (!det || det.token.length < 2) {
      setMentionBox(null);
    } else {
      const ctrl = new AbortController();
      mentionAbortRef.current = ctrl;
      fetch(`/api/users/search?q=${encodeURIComponent(det.token)}`, { signal: ctrl.signal, credentials: 'include' })
        .then((r) => (r.ok ? r.json() : { users: [] }))
        .then((data) => {
          if (ctrl.signal.aborted) return;
          const results = Array.isArray(data?.users) ? data.users.slice(0, 6) : [];
          setMentionBox(results.length ? { results, activeIndex: 0 } : null);
        })
        .catch(() => {});
    }
    if (!friend?.id) return;
    // Clearing the input after typing — proactively tell the friend we
    // stopped so their indicator clears immediately rather than after TTL.
    if (!v.trim()) {
      if (prev.trim() && lastTypingFriendRef.current === friend.id) {
        ctx.notifyStoppedTyping?.(friend.id);
        lastTypingFriendRef.current = null;
        lastTypingSentRef.current = 0;
      }
      return;
    }
    const now = Date.now();
    // Throttle to once every 2 s. The receiver TTL is 4 s, so each ping
    // refreshes well before the indicator would expire mid-typing.
    if (now - lastTypingSentRef.current < 2000) return;
    lastTypingSentRef.current = now;
    lastTypingFriendRef.current = friend.id;
    ctx.notifyTyping?.(friend.id);
  };

  // Switching conversations (or unmounting) must drop any open mention picker
  // and cancel its in-flight search so stale suggestions can't leak into the
  // newly opened thread.
  useEffect(() => {
    setMentionBox(null);
    return () => {
      if (mentionAbortRef.current) { try { mentionAbortRef.current.abort(); } catch {} mentionAbortRef.current = null; }
    };
  }, [friend?.id]);

  const sendMessagePayload = useCallback(async (payload) => {
    const res = await fetch('/api/messages', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    return res;
  }, []);

  const stopRecordingTimer = () => {
    if (recordTimerRef.current) {
      clearInterval(recordTimerRef.current);
      recordTimerRef.current = null;
    }
  };

  const computeElapsedMs = () => {
    const segment = recordPausedRef.current
      ? 0
      : Math.max(0, Date.now() - recordStartRef.current);
    return recordAccumRef.current + segment;
  };

  const cleanupRecorderStream = () => {
    const rec = recorderRef.current;
    if (rec?.stream) {
      try { rec.stream.getTracks().forEach((t) => t.stop()); } catch {}
    }
    recorderRef.current = null;
  };

  const stopAudioMeter = () => {
    if (audioRafRef.current != null && typeof cancelAnimationFrame !== 'undefined') {
      try { cancelAnimationFrame(audioRafRef.current); } catch {}
    }
    audioRafRef.current = null;
    analyserRef.current = null;
    const ac = audioContextRef.current;
    audioContextRef.current = null;
    if (ac && typeof ac.close === 'function') {
      try { ac.close(); } catch {}
    }
  };

  const startAudioMeter = (stream) => {
    if (typeof window === 'undefined') return;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC || typeof requestAnimationFrame === 'undefined') return;
    let ctx;
    try { ctx = new AC(); } catch { return; }
    let source;
    try {
      source = ctx.createMediaStreamSource(stream);
    } catch {
      try { ctx.close(); } catch {}
      return;
    }
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.7;
    source.connect(analyser);
    audioContextRef.current = ctx;
    analyserRef.current = analyser;
    const data = new Uint8Array(analyser.fftSize);
    let last = 0;
    const tick = (t) => {
      // Analyser was torn down (stop / cancel / error / unmount) — bail out.
      if (analyserRef.current !== analyser) return;
      audioRafRef.current = requestAnimationFrame(tick);
      // Throttle to ~14fps so we don't churn React on every paint.
      if (t - last < 70) return;
      last = t;
      // While the take is paused, freeze the visualizer at zero so the
      // indicator visibly "flatlines" — even though the underlying mic
      // stream is still live, we shouldn't be drawing new activity.
      if (recordPausedRef.current) {
        setAudioLevels((prev) => {
          const next = prev.slice(1);
          next.push(0);
          return next;
        });
        return;
      }
      try { analyser.getByteTimeDomainData(data); } catch { return; }
      let sum = 0;
      for (let i = 0; i < data.length; i++) {
        const v = (data[i] - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / data.length);
      const level = Math.min(1, rms * 3.2);
      setAudioLevels((prev) => {
        const next = prev.slice(1);
        next.push(level);
        return next;
      });
    };
    audioRafRef.current = requestAnimationFrame(tick);
  };

  // Translate the structured errors thrown by uploadVoiceBlob and the
  // /api/messages POST into specific user-facing messages so distinct
  // failure modes (storage down, not friends, signed PUT failed, server
  // insert error, network drop) no longer collapse to the same generic
  // "Could not send voice note." string. The original error is also
  // surfaced via console.error so it remains diagnosable from logs.
  const messageForSendError = (err) => {
    const stage = err?.stage;
    const code = err?.code;
    const status = err?.status;
    if (stage === 'request-url') {
      if (status === 401) return 'You need to sign in again to send voice notes.';
      if (status === 413) return 'Voice note is too long. Try a shorter recording.';
      if (status === 503) {
        if (code === 'storage_sidecar_unreachable') {
          return "Voice storage isn't reachable right now. Please try again in a moment.";
        }
        return 'Voice storage is temporarily unavailable. Please try again later.';
      }
      return "Couldn't get an upload slot. Please check your connection and try again.";
    }
    if (stage === 'put') {
      if (err?.network) return "Couldn't upload the voice note — please check your connection.";
      return `Voice note upload failed${status ? ` (HTTP ${status})` : ''}. Please try again.`;
    }
    if (stage === 'message') {
      if (status === 401) return 'You need to sign in again to send voice notes.';
      if (status === 403) return 'You can only message friends.';
      if (status === 400) return 'This voice note was rejected by the server.';
      return "Voice note uploaded but the server couldn't save it. Please try again.";
    }
    return 'Could not send voice note.';
  };

  // Returns `{ ok, message }`. The caller (handleSendPreview / its retry
  // closure) cares about both: `ok` drives whether to clear the cached
  // preview blob, and `message` is the per-stage user-facing string we
  // push into the failure toast alongside the Try-again action.
  const sendVoiceBlob = async (blob, durationMs, peaks) => {
    if (!friend?.id) return { ok: false, message: 'Could not send voice note.' };
    setSending(true);
    setSendError(null);
    setVoiceError(null);
    try {
      const { attachmentUrl, attachmentDurationMs } = await uploadVoiceBlob(blob, durationMs);
      const cleanPeaks = Array.isArray(peaks) && peaks.length > 0 ? peaks : null;
      let res;
      try {
        res = await sendMessagePayload({
          receiverId: friend.id,
          content: '',
          messageType: 'voice',
          attachmentUrl,
          attachmentDurationMs,
          attachmentPeaks: cleanPeaks,
        });
      } catch (netErr) {
        const wrapped = new Error('voice-send-message-network');
        wrapped.stage = 'message';
        wrapped.network = true;
        wrapped.cause = netErr;
        throw wrapped;
      }
      if (!res.ok) {
        const wrapped = new Error('voice-send-message-failed');
        wrapped.stage = 'message';
        wrapped.status = res.status;
        try {
          const body = await res.clone().json();
          if (body?.error) wrapped.serverMessage = body.error;
          if (body?.code) wrapped.code = body.code;
        } catch (_e) {}
        throw wrapped;
      }
      const data = await res.json();
      if (data?.message) {
        setThread((prev) => [...prev, data.message]);
        if (typeof window !== 'undefined') {
          const m = data.message;
          window.dispatchEvent(
            new CustomEvent('piks:message:new', {
              detail: {
                id: m.id,
                senderId: m.senderId,
                receiverId: m.receiverId,
                content: m.content,
                messageType: m.messageType || 'voice',
                attachmentUrl: m.attachmentUrl || attachmentUrl,
                attachmentDurationMs: m.attachmentDurationMs ?? attachmentDurationMs,
                attachmentPeaks: m.attachmentPeaks ?? cleanPeaks,
                createdAt:
                  m.createdAt instanceof Date
                    ? m.createdAt.toISOString()
                    : m.createdAt,
              },
            })
          );
        }
      }
      ctx.refresh?.();
      return { ok: true };
    } catch (err) {
      console.error('[voice-note] send failed:', {
        stage: err?.stage || 'unknown',
        status: err?.status,
        code: err?.code,
        serverMessage: err?.serverMessage,
        network: !!err?.network,
        message: err?.message,
      });
      const message = messageForSendError(err);
      setSendError(message);
      return { ok: false, message };
    } finally {
      setSending(false);
    }
  };

  const uploadVoiceBlob = async (blob, durationMs) => {
    const ext = blob.type.includes('wav') ? 'wav'
      : blob.type.includes('mp4') ? 'm4a'
      : blob.type.includes('ogg') ? 'ogg'
      : 'webm';
    const filename = `voice-${Date.now()}.${ext}`;
    // Strip the codec parameter from the MIME (e.g. `audio/webm;codecs=opus`
    // → `audio/webm`) before signing/uploading. The bare type is what other
    // browsers expect to see when the file is later re-served, so stashing
    // the parameter-laden value in object-storage metadata isn't worth the
    // playback risk for receivers on Safari.
    const baseMime = (blob.type || 'audio/webm').split(';')[0].trim() || 'audio/webm';
    // Re-wrap the blob as a File so the Vercel Blob client `upload()`
    // sees a filename + a normalized content type. The recorder hands us
    // a MediaRecorder Blob (no name, sometimes `audio/webm;codecs=opus`),
    // which the server-side `allowedContentTypes` check would reject.
    const fileForUpload =
      typeof File !== 'undefined'
        ? new File([blob], filename, { type: baseMime })
        : blob;
    try {
      const { uploadToBlob } = await import('../../utils/blobUpload');
      const { url } = await uploadToBlob(fileForUpload, { kind: 'voice-note' });
      return { attachmentUrl: url, attachmentDurationMs: durationMs };
    } catch (err) {
      const wrapped = new Error('voice-upload-failed');
      wrapped.stage = 'upload';
      wrapped.status = err?.status || 0;
      wrapped.serverMessage = err?.message;
      wrapped.cause = err;
      throw wrapped;
    }
  };

  const handleStartRecording = async ({ continuingSegment = false } = {}) => {
    if (recording || sending) return;
    setVoiceError(null);
    if (!continuingSegment) setSendError(null);
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setVoiceError('Recording not supported on this device.');
      return;
    }
    if (typeof window === 'undefined' || typeof window.MediaRecorder === 'undefined') {
      setVoiceError('Recording not supported on this device.');
      return;
    }
    let stream = null;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      let mimeType = 'audio/webm';
      try {
        if (!window.MediaRecorder.isTypeSupported('audio/webm')) {
          if (window.MediaRecorder.isTypeSupported('audio/mp4')) mimeType = 'audio/mp4';
          else mimeType = '';
        }
      } catch { mimeType = ''; }
      const recorder = mimeType
        ? new window.MediaRecorder(stream, { mimeType })
        : new window.MediaRecorder(stream);
      recordMimeRef.current = recorder.mimeType || mimeType || 'audio/webm';
      recordChunksRef.current = [];
      recordCancelledRef.current = false;
      // Preserve cumulative duration + previously finalized segments when
      // appending another segment to the same take. For a fresh take, wipe
      // the slate clean.
      if (!continuingSegment) {
        recordAccumRef.current = 0;
        segmentsRef.current = [];
      }
      stopReasonRef.current = null;
      recordPausedRef.current = false;
      selfPauseExpectedRef.current = false;
      setPaused(false);
      setPauseReason(null);
      setPauseSupported(
        typeof recorder.pause === 'function' && typeof recorder.resume === 'function'
      );
      recorder.ondataavailable = (ev) => {
        if (ev.data && ev.data.size > 0) recordChunksRef.current.push(ev.data);
      };
      // Detect pauses we didn't initiate ourselves — e.g. another app grabs
      // the microphone, the device locks, or the user takes a phone call.
      // Our own pauses set selfPauseExpectedRef just before calling pause(),
      // so the unexpected branch only runs for true external interruptions.
      recorder.onpause = () => {
        if (selfPauseExpectedRef.current) {
          selfPauseExpectedRef.current = false;
          return;
        }
        // Sync our internal accounting the same way handlePauseRecording
        // would have, so the elapsed timer freezes correctly.
        if (!recordPausedRef.current) {
          recordAccumRef.current += Math.max(0, Date.now() - recordStartRef.current);
          recordPausedRef.current = true;
          setPaused(true);
          setRecordElapsed(recordAccumRef.current);
          setAudioLevels(new Array(LEVEL_BAR_COUNT).fill(0));
        }
        setPauseReason('system');
      };
      // Track-level interruptions (mic source going mute/ended) sometimes
      // happen *without* the MediaRecorder transitioning to paused on its
      // own — typically when another app temporarily grabs the device.
      // Pause it ourselves so the visualizer/timer freeze and the user
      // sees a hint instead of a silent dead-air recording.
      const interruptHandler = () => {
        const r = recorderRef.current;
        if (!r) return;
        if (r.state === 'recording' && !recordPausedRef.current) {
          try {
            selfPauseExpectedRef.current = true;
            r.pause();
          } catch {
            selfPauseExpectedRef.current = false;
          }
          recordAccumRef.current += Math.max(0, Date.now() - recordStartRef.current);
          recordPausedRef.current = true;
          setPaused(true);
          setRecordElapsed(recordAccumRef.current);
          setAudioLevels(new Array(LEVEL_BAR_COUNT).fill(0));
        }
        setPauseReason('system');
      };
      try {
        const tracks = typeof stream.getAudioTracks === 'function'
          ? stream.getAudioTracks()
          : [];
        tracks.forEach((track) => {
          try { track.addEventListener('mute', interruptHandler); } catch {}
          try { track.addEventListener('ended', interruptHandler); } catch {}
        });
      } catch {}
      recorder.onstop = () => {
        try { stopRecordingTimer(); } catch {}
        try { stopAudioMeter(); } catch {}
        const elapsed = computeElapsedMs();
        const chunks = recordChunksRef.current;
        recordChunksRef.current = [];
        recordPausedRef.current = false;
        selfPauseExpectedRef.current = false;
        // 'preview-segment' means the user tapped Done on the segment-mode
        // (Safari/iOS) fallback path; the take should remain Continue-able.
        // Anything else is a final stop and we wipe segment state below.
        const reason = stopReasonRef.current;
        stopReasonRef.current = null;
        const isSegmentStop = reason === 'preview-segment';
        try { cleanupRecorderStream(); } catch {}
        setRecording(false);
        setPaused(false);
        setPauseReason(null);
        setAudioLevels(new Array(LEVEL_BAR_COUNT).fill(0));
        if (recordCancelledRef.current) {
          recordCancelledRef.current = false;
          recordAccumRef.current = 0;
          segmentsRef.current = [];
          setRecordElapsed(0);
          return;
        }
        const mime = recordMimeRef.current || 'audio/webm';
        // Append the just-finalized chunks as the next segment (if any).
        // For segment-mode stops we'll concat all segments below; for the
        // legacy single-stop path the array holds a single entry which we
        // still treat as the preview blob.
        if (chunks.length) {
          const segBlob = new Blob(chunks, { type: mime });
          segmentsRef.current.push({ blob: segBlob, mime });
        }
        if (!segmentsRef.current.length) {
          setVoiceError('Recording was empty. Try again.');
          recordAccumRef.current = 0;
          setRecordElapsed(0);
          return;
        }
        // Single-segment fast path: avoid an unnecessary decode/re-encode
        // round-trip and surface the original blob just like the pre-fallback
        // behavior did. This also keeps the existing e2e expectations
        // (audio/webm, audio/mp4 → m4a) intact for one-shot recordings.
        if (segmentsRef.current.length === 1) {
          const only = segmentsRef.current[0];
          let url = '';
          try {
            if (typeof URL !== 'undefined' && URL.createObjectURL) {
              url = URL.createObjectURL(only.blob);
            }
          } catch {}
          setVoicePreview({
            blob: only.blob,
            url,
            durationMs: elapsed,
            mime: only.mime,
            trimStartMs: 0,
            trimEndMs: elapsed,
          });
          if (isSegmentStop) {
            // Keep the cumulative time alive so a subsequent Continue tap
            // appends to the right place on the timer + max-duration check.
            recordAccumRef.current = elapsed;
            setResumableTake(elapsed < MAX_VOICE_MS - 200);
          } else {
            recordAccumRef.current = 0;
            segmentsRef.current = [];
            setResumableTake(false);
            setRecordElapsed(0);
          }
          return;
        }
        // Multi-segment: decode every segment and stitch them into one
        // playable WAV blob. Async, so we flip a brief "Processing…" state
        // to keep the composer from snapping back to the text input mid-build.
        setProcessingPreview(true);
        (async () => {
          let previewBlob = null;
          let previewMime = 'audio/wav';
          try {
            previewBlob = await concatVoiceSegmentsToWav(segmentsRef.current);
          } catch {
            previewBlob = null;
          }
          if (!previewBlob) {
            // Decoding failed (rare on Safari with its own audio/mp4, but
            // possible if a segment came through corrupted). Fall back to
            // just the latest segment so the user isn't stuck with nothing —
            // they lose earlier audio but can still send something. Surface
            // a clear error so they know to re-record if they want it all.
            const last = segmentsRef.current[segmentsRef.current.length - 1];
            segmentsRef.current = [last];
            previewBlob = last.blob;
            previewMime = last.mime;
            setVoiceError("Couldn't combine takes — only the latest segment is shown.");
          }
          let url = '';
          try {
            if (typeof URL !== 'undefined' && URL.createObjectURL) {
              url = URL.createObjectURL(previewBlob);
            }
          } catch {}
          setProcessingPreview(false);
          setVoicePreview({
            blob: previewBlob,
            url,
            durationMs: elapsed,
            mime: previewMime,
            trimStartMs: 0,
            trimEndMs: elapsed,
          });
          if (isSegmentStop) {
            recordAccumRef.current = elapsed;
            setResumableTake(elapsed < MAX_VOICE_MS - 200);
          } else {
            recordAccumRef.current = 0;
            // Replace the now-stitched segments with a single WAV entry so a
            // hypothetical future Continue would only have one segment to
            // re-decode. Practically not reached because isSegmentStop=false
            // means resumable=false too, but keeps state coherent.
            segmentsRef.current = [{ blob: previewBlob, mime: previewMime }];
            setResumableTake(false);
            setRecordElapsed(0);
          }
        })();
      };
      recorderRef.current = recorder;
      recordStartRef.current = Date.now();
      // On a continue-segment start the visible counter should pick up from
      // the cumulative time so far; on a fresh take it starts at 0.
      setRecordElapsed(continuingSegment ? recordAccumRef.current : 0);
      setAudioLevels(new Array(LEVEL_BAR_COUNT).fill(0));
      recorder.start();
      setRecording(true);
      try { startAudioMeter(stream); } catch {}
      recordTimerRef.current = setInterval(() => {
        const elapsed = computeElapsedMs();
        setRecordElapsed(elapsed);
        if (elapsed >= MAX_VOICE_MS) {
          try { recorder.stop(); } catch {}
        }
      }, 100);
    } catch (err) {
      try { stopRecordingTimer(); } catch {}
      try { stopAudioMeter(); } catch {}
      if (stream) {
        try { stream.getTracks().forEach((t) => t.stop()); } catch {}
      }
      recorderRef.current = null;
      recordChunksRef.current = [];
      recordCancelledRef.current = false;
      // Wipe cumulative state on any failed start. On the continue-segment
      // path the preview was already cleared by handleContinueRecording, so
      // keeping segments around without a way to resurface them as a preview
      // would just confuse the user — better to fall back to a clean slate
      // and let them try again from scratch.
      recordAccumRef.current = 0;
      segmentsRef.current = [];
      stopReasonRef.current = null;
      recordPausedRef.current = false;
      selfPauseExpectedRef.current = false;
      setRecording(false);
      setPaused(false);
      setPauseReason(null);
      setRecordElapsed(0);
      setAudioLevels(new Array(LEVEL_BAR_COUNT).fill(0));
      const name = err?.name || '';
      if (name === 'NotAllowedError' || name === 'SecurityError') {
        setVoiceError('Microphone access denied.');
      } else if (name === 'NotFoundError' || name === 'OverconstrainedError') {
        setVoiceError('No microphone available.');
      } else {
        setVoiceError('Could not start recording. Try again.');
      }
    }
  };

  // Build a preview blob from the chunks captured so far (after a flushing
  // requestData) and surface it via voicePreview. The recorder/stream stay
  // alive in the paused state so a subsequent "Continue" can resume the same
  // continuous take. durationMs is the elapsed time captured at pause.
  const finalizePreviewFromChunks = (durationMs) => {
    const chunks = recordChunksRef.current;
    if (!chunks.length) {
      setVoiceError('Recording was empty. Try again.');
      tearDownLingeringRecorder();
      return;
    }
    const mime = recordMimeRef.current || 'audio/webm';
    const blob = new Blob(chunks, { type: mime });
    let url = '';
    try {
      if (typeof URL !== 'undefined' && URL.createObjectURL) {
        url = URL.createObjectURL(blob);
      }
    } catch {}
    setVoicePreview({
      blob,
      url,
      durationMs,
      mime,
      trimStartMs: 0,
      trimEndMs: durationMs,
    });
    setRecording(false);
    // Only enable Continue if there's still recording budget; otherwise the
    // user has already maxed out and resuming would just immediately stop.
    setResumableTake(durationMs < MAX_VOICE_MS - 200);
  };

  // Pause the live recorder and capture a preview blob from the data flushed
  // by requestData(). Throws if the underlying calls fail; the caller should
  // fall back to a full stop in that case so the take isn't lost.
  const pauseForPreview = (rec) => {
    const wasRecording = rec.state === 'recording';
    if (wasRecording) {
      // Same accounting handlePauseRecording does — snapshot the in-flight
      // segment into the accumulator so durationMs is accurate.
      recordAccumRef.current += Math.max(0, Date.now() - recordStartRef.current);
    }
    recordPausedRef.current = true;
    const elapsedAtPause = recordAccumRef.current;
    const onceData = () => {
      rec.removeEventListener('dataavailable', onceData);
      // Defer one task so the property ondataavailable handler also gets to
      // push the flushed chunk into recordChunksRef before we read it.
      setTimeout(() => finalizePreviewFromChunks(elapsedAtPause), 0);
    };
    rec.addEventListener('dataavailable', onceData);
    try {
      rec.requestData();
    } catch (e) {
      rec.removeEventListener('dataavailable', onceData);
      throw e;
    }
    if (wasRecording) {
      try {
        // Mark this pause as self-initiated so onpause doesn't misread it
        // as an external/system interruption.
        selfPauseExpectedRef.current = true;
        rec.pause();
      } catch (e) {
        selfPauseExpectedRef.current = false;
        rec.removeEventListener('dataavailable', onceData);
        throw e;
      }
    }
    stopRecordingTimer();
    setPaused(true);
    setRecordElapsed(elapsedAtPause);
  };

  // Synchronously dispose of the lingering paused recorder. We detach
  // handlers and refs first so the async onstop can't clobber state from a
  // freshly-started take (e.g. when the user immediately taps Re-record).
  const tearDownLingeringRecorder = () => {
    const rec = recorderRef.current;
    if (!rec) {
      // Even with no live recorder we may still be holding onto stitched
      // segments from a Safari/iOS take that was previewed but not sent —
      // wipe them so a fresh take or friend switch starts clean.
      segmentsRef.current = [];
      stopReasonRef.current = null;
      return;
    }
    recorderRef.current = null;
    try { rec.ondataavailable = null; } catch {}
    try { rec.onstop = null; } catch {}
    try { rec.onpause = null; } catch {}
    try { stopRecordingTimer(); } catch {}
    try { stopAudioMeter(); } catch {}
    try { if (rec.state !== 'inactive') rec.stop(); } catch {}
    if (rec.stream) {
      try { rec.stream.getTracks().forEach((t) => t.stop()); } catch {}
    }
    recordChunksRef.current = [];
    recordAccumRef.current = 0;
    segmentsRef.current = [];
    stopReasonRef.current = null;
    recordPausedRef.current = false;
    selfPauseExpectedRef.current = false;
    recordCancelledRef.current = false;
    setRecording(false);
    setPaused(false);
    setPauseReason(null);
    setRecordElapsed(0);
    setProcessingPreview(false);
    setAudioLevels(new Array(LEVEL_BAR_COUNT).fill(0));
  };

  const handleStopRecording = () => {
    const rec = recorderRef.current;
    if (!rec || rec.state === 'inactive') return;
    recordCancelledRef.current = false;
    // Prefer keeping the recorder alive (paused) so the preview row can offer
    // "Continue" — appending more audio to the same take instead of starting
    // a new one. Requires browser support for pause + requestData.
    if (
      pauseSupported &&
      (rec.state === 'recording' || rec.state === 'paused') &&
      typeof rec.requestData === 'function' &&
      typeof rec.pause === 'function'
    ) {
      try {
        pauseForPreview(rec);
        return;
      } catch {
        // Pause/flush failed at runtime even though the methods exist (some
        // Safari/iOS builds throw here). Fall through to the segment-stitch
        // fallback below so Continue is still available — onstop will push
        // the chunks as a segment and a subsequent Continue tap spins up a
        // fresh recorder for the appended audio.
      }
    }
    // Segment-stitch fallback (Safari/iOS path): finalize the recorder fully
    // and tell onstop this is a *segment* stop so the preview still allows
    // Continue. The new segment is concatenated with any prior segments into
    // a single playable WAV blob inside onstop.
    stopReasonRef.current = 'preview-segment';
    try { rec.stop(); } catch {
      stopReasonRef.current = null;
    }
  };

  const handlePauseRecording = () => {
    const rec = recorderRef.current;
    if (!rec || rec.state !== 'recording' || recordPausedRef.current) return;
    if (typeof rec.pause !== 'function') return;
    // Mark this pause as self-initiated so the recorder's onpause handler
    // doesn't misclassify it as an external/system interruption.
    selfPauseExpectedRef.current = true;
    try { rec.pause(); } catch {
      selfPauseExpectedRef.current = false;
      return;
    }
    // Snapshot the current segment's elapsed ms into the accumulator and
    // freeze the visible timer at that value. computeElapsedMs() returns
    // just the accumulator while paused.
    recordAccumRef.current += Math.max(0, Date.now() - recordStartRef.current);
    recordPausedRef.current = true;
    setPaused(true);
    setRecordElapsed(recordAccumRef.current);
    setAudioLevels(new Array(LEVEL_BAR_COUNT).fill(0));
  };

  const handleResumeRecording = () => {
    const rec = recorderRef.current;
    if (!rec || !recordPausedRef.current) return;
    if (typeof rec.resume !== 'function') return;
    try { rec.resume(); } catch { return; }
    // Re-base the segment start so computeElapsedMs() resumes counting
    // from the point we paused at, never losing the prior take.
    recordStartRef.current = Date.now();
    recordPausedRef.current = false;
    setPaused(false);
    setPauseReason(null);
  };

  const handleCancelRecording = () => {
    const rec = recorderRef.current;
    if (!rec) return;
    recordCancelledRef.current = true;
    // stop() works on both 'recording' and 'paused' states and finalizes
    // the take, firing onstop where we'll discard the chunks.
    try { if (rec.state !== 'inactive') rec.stop(); } catch {}
  };

  const clearVoicePreview = useCallback(() => {
    setVoicePreview((prev) => {
      if (prev?.url) {
        try { URL.revokeObjectURL(prev.url); } catch {}
      }
      return null;
    });
  }, []);

  // Update the trim window on the active preview without touching the
  // underlying blob/recorder — Continue still resumes from the full take
  // because the recorder chunks aren't mutated by trimming.
  const setVoicePreviewTrim = useCallback((trimStartMs, trimEndMs) => {
    setVoicePreview((prev) => {
      if (!prev) return prev;
      return { ...prev, trimStartMs, trimEndMs };
    });
  }, []);

  // Captured by VoiceWaveform during the preview decode. We stash the peaks
  // on the preview so handleSendPreview can ship them with the upload —
  // without this the receiver's bubble would have to fetch+decode the audio
  // again on mount just to redraw the same bars we already computed here.
  const setVoicePreviewPeaks = useCallback((peaks) => {
    if (!Array.isArray(peaks) || peaks.length === 0) return;
    setVoicePreview((prev) => {
      if (!prev) return prev;
      // Bail if we already have an identical-length array — avoids a
      // setState churn loop now that VoiceWaveform's effect re-fires
      // whenever the parent rerenders with new props.
      if (prev.peaks && prev.peaks.length === peaks.length) return prev;
      return { ...prev, peaks };
    });
  }, []);

  // Discard the current take and immediately arm the recorder for a fresh
  // attempt — saves the user a tap when they didn't like what they heard.
  const handleRerecordPreview = () => {
    if (sending || recording) return;
    setVoiceError(null);
    setSendError(null);
    tearDownLingeringRecorder();
    setResumableTake(false);
    clearVoicePreview();
    handleStartRecording();
  };

  // Resume the same take from the preview row. Two paths:
  //   1. Alive-recorder path (Chrome/Firefox/etc): the existing MediaRecorder
  //      is still in the 'paused' state from pauseForPreview — just resume it
  //      so the new audio appends to the existing chunks as one clip.
  //   2. Segment-stitch path (Safari/iOS where pause+requestData isn't
  //      usable): the prior recorder was fully stopped after Done; the
  //      preview blob is the latest segment (or the WAV concat of all so
  //      far). Spin up a fresh MediaRecorder while preserving recordAccumRef
  //      and segmentsRef so the timer continues from the cumulative time
  //      and onstop concatenates the new chunk onto the rest on next stop.
  const handleContinueRecording = () => {
    if (sending || recording) return;
    const rec = recorderRef.current;
    // Drop the preview blob (revoke its URL) without touching segmentsRef —
    // those segment blobs are the prefix of the final clip.
    setVoicePreview((prev) => {
      if (prev?.url) {
        try { URL.revokeObjectURL(prev.url); } catch {}
      }
      return null;
    });
    setVoiceError(null);
    setSendError(null);
    setResumableTake(false);

    if (rec && rec.state === 'paused') {
      try {
        rec.resume();
      } catch {
        // Couldn't resume — fall back to a clean slate so the user isn't stuck.
        tearDownLingeringRecorder();
        return;
      }
      recordStartRef.current = Date.now();
      recordPausedRef.current = false;
      setPaused(false);
      // Clear any stale pause hint (e.g. the take was system-paused before
      // the user previewed it) so a later manual pause doesn't accidentally
      // re-show a system/tab hint that no longer applies.
      setPauseReason(null);
      setRecording(true);
      // Restart the visible elapsed timer (we cleared it during
      // pauseForPreview) so the running counter and MAX_VOICE_MS auto-stop
      // continue working.
      recordTimerRef.current = setInterval(() => {
        const elapsed = computeElapsedMs();
        setRecordElapsed(elapsed);
        if (elapsed >= MAX_VOICE_MS) {
          try { rec.stop(); } catch {}
        }
      }, 100);
      return;
    }

    // Segment-stitch fallback: previous recorder was finalized at the last
    // Done tap, so segmentsRef holds the prior take(s) and recordAccumRef
    // holds the cumulative duration. Open a fresh recorder; its onstop will
    // push the new chunk onto segmentsRef and rebuild the concat preview.
    if (segmentsRef.current.length > 0) {
      handleStartRecording({ continuingSegment: true });
    }
  };

  const handleSendPreview = async () => {
    if (sending) return;
    if (!voicePreview?.blob) return;
    const { blob, durationMs, trimStartMs, trimEndMs, peaks } = voicePreview;
    const startMs = Math.max(0, Math.min(durationMs, trimStartMs ?? 0));
    const endMs = Math.max(startMs, Math.min(durationMs, trimEndMs ?? durationMs));
    const trimmedDuration = Math.max(0, endMs - startMs);
    let outBlob = blob;
    let outDuration = durationMs;
    // Default to the peaks captured during the preview decode. When the
    // user trimmed the take we recompute peaks from the trimmed buffer
    // below so the bubble's bars line up with the audio that actually
    // went out the door.
    let outPeaks = peaks || null;
    // Only re-encode when the user actually trimmed something meaningful;
    // otherwise the existing fast path uploads the original codec untouched.
    if (startMs > 50 || endMs < durationMs - 50) {
      try {
        const trimResult = await trimVoiceBlobToWav(blob, startMs, endMs);
        if (trimResult && trimResult.blob) {
          outBlob = trimResult.blob;
          outDuration = trimmedDuration;
          if (Array.isArray(trimResult.peaks) && trimResult.peaks.length > 0) {
            outPeaks = trimResult.peaks;
          }
        }
      } catch (err) {
        // If decoding/encoding fails for any reason, fall back to sending
        // the original take rather than blocking the user. The trim was a
        // UX nicety — losing it is recoverable, losing the take isn't.
      }
    }
    await attemptSendVoice(outBlob, outDuration, outPeaks);
  };

  // Single send-attempt entry point shared by the initial Send tap and any
  // subsequent retries surfaced from the failure toast. On success it tears
  // down the recorder + clears the cached preview blob so the composer
  // returns to its idle state. On failure it leaves the cached blob + trim
  // window in place (so the inline composer error label remains a fallback)
  // and pushes a toast carrying the per-stage failure message plus a
  // Try-again action whose retry callback re-runs *this* function with the
  // same already-trimmed buffer — no re-record, no re-trim, one tap.
  const attemptSendVoice = async (outBlob, outDuration, outPeaks) => {
    const result = await sendVoiceBlob(outBlob, outDuration, outPeaks);
    if (result?.ok) {
      // Drop any lingering error toast from an earlier failed attempt so
      // the success isn't visually contradicted by a still-visible red card.
      if (lastVoiceErrorToastIdRef.current) {
        ctx.dismissToast?.(lastVoiceErrorToastIdRef.current);
        lastVoiceErrorToastIdRef.current = null;
      }
      tearDownLingeringRecorder();
      setResumableTake(false);
      clearVoicePreview();
      return true;
    }
    const errorMessage = result?.message || 'Could not send voice note.';
    if (typeof ctx?.enqueueToast === 'function') {
      // Replace any previous voice-error toast so repeated Send taps don't
      // stack a column of identical red cards. Each new failure gets its
      // own time-stamped id (the seen-set in NotificationsContext dedups
      // by id and would otherwise swallow the replacement).
      if (lastVoiceErrorToastIdRef.current) {
        ctx.dismissToast?.(lastVoiceErrorToastIdRef.current);
      }
      const toastId = `voice_send_error:${Date.now()}`;
      lastVoiceErrorToastIdRef.current = toastId;
      ctx.enqueueToast({
        id: toastId,
        type: 'voice_send_error',
        // Persistent — the toast carries an action and shouldn't time out
        // before the user has a chance to read the reason and tap Try again.
        // Manual dismiss + the Try-again button still close it normally.
        persistent: true,
        payload: {
          message: errorMessage,
          retry: () => attemptSendVoice(outBlob, outDuration, outPeaks),
        },
      });
    }
    return false;
  };

  useEffect(() => {
    return () => {
      stopRecordingTimer();
      stopAudioMeter();
      const rec = recorderRef.current;
      if (rec && rec.state !== 'inactive') {
        recordCancelledRef.current = true;
        try { rec.stop(); } catch {}
      }
      recordAccumRef.current = 0;
      segmentsRef.current = [];
      stopReasonRef.current = null;
      recordPausedRef.current = false;
      cleanupRecorderStream();
      setVoicePreview((prev) => {
        if (prev?.url) {
          try { URL.revokeObjectURL(prev.url); } catch {}
        }
        return null;
      });
      // Drop any lingering voice-error toast — its retry callback closes
      // over state from this conversation that won't be valid anymore.
      if (lastVoiceErrorToastIdRef.current) {
        ctxRef.current?.dismissToast?.(lastVoiceErrorToastIdRef.current);
        lastVoiceErrorToastIdRef.current = null;
      }
    };
  }, []);

  // Auto-pause an in-progress take when the messenger tab becomes hidden or
  // the window loses focus (e.g. the user clicks into another app on the same
  // desktop without backgrounding the tab) so we don't silently capture dead
  // air while the user is elsewhere. The recorder stays paused on return —
  // the user must tap Resume to continue. handlePauseRecording is itself a
  // no-op if the recorder isn't actively recording or pause isn't supported,
  // so the guard here is just to avoid attaching the listener when there's
  // nothing to pause.
  useEffect(() => {
    if (!recording || paused) return undefined;
    if (typeof document === 'undefined') return undefined;
    // Mirrors handlePauseRecording's own guards so we only mark a pause as
    // "auto" when it actually took effect (rec was live and pause is
    // supported). Otherwise the hint could appear without an actual pause.
    const pauseFromAuto = () => {
      const rec = recorderRef.current;
      if (!rec || rec.state !== 'recording' || recordPausedRef.current) return;
      handlePauseRecording();
      if (recordPausedRef.current) setPauseReason('tab');
    };
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'hidden') return;
      pauseFromAuto();
    };
    const onWindowBlur = () => {
      pauseFromAuto();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    if (typeof window !== 'undefined') {
      window.addEventListener('blur', onWindowBlur);
    }
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (typeof window !== 'undefined') {
        window.removeEventListener('blur', onWindowBlur);
      }
    };
  }, [recording, paused]);

  // Switching to a different conversation should drop any pending preview so
  // the recorded blob isn't accidentally sent to the wrong friend.
  useEffect(() => {
    tearDownLingeringRecorder();
    setResumableTake(false);
    clearVoicePreview();
    setVoiceError(null);
    // The error toast carries a retry closure tied to the previous
    // conversation's blob — drop it so a stale Try-again tap can't ship
    // audio to the wrong friend.
    if (lastVoiceErrorToastIdRef.current) {
      ctx.dismissToast?.(lastVoiceErrorToastIdRef.current);
      lastVoiceErrorToastIdRef.current = null;
    }
    // tearDownLingeringRecorder is intentionally not in deps — it's defined
    // inline each render and only needs to fire when the active friend
    // changes (same intent as the existing clearVoicePreview behavior).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [friend?.id, clearVoicePreview]);

  const handleSend = async (e) => {
    e?.preventDefault?.();
    const text = reply.trim();
    if (!text || !friend?.id || sending) return;
    setMentionBox(null);
    setSending(true);
    setSendError(null);
    try {
      const res = await sendMessagePayload({ receiverId: friend.id, content: text });
      if (!res.ok) {
        setSendError(res.status === 403 ? 'You can only message friends.' : 'Could not send.');
        return;
      }
      const data = await res.json();
      if (data?.message) {
        setThread((prev) => [...prev, data.message]);
        if (typeof window !== 'undefined') {
          const m = data.message;
          window.dispatchEvent(
            new CustomEvent('piks:message:new', {
              detail: {
                id: m.id,
                senderId: m.senderId,
                receiverId: m.receiverId,
                content: m.content,
                createdAt:
                  m.createdAt instanceof Date
                    ? m.createdAt.toISOString()
                    : m.createdAt,
              },
            })
          );
        }
      }
      setReply('');
      // Sending implicitly ends the typing session — tell the friend so their
      // indicator clears the moment our message lands, not 4 s later. Also
      // reset the throttle so a follow-up message broadcasts on first stroke.
      if (lastTypingFriendRef.current === friend.id) {
        ctx.notifyStoppedTyping?.(friend.id);
        lastTypingFriendRef.current = null;
      }
      lastTypingSentRef.current = 0;
      ctx.refresh?.();
    } catch {
      setSendError('Could not send.');
    } finally {
      setSending(false);
    }
  };

  const cardBorder = isLight ? 'rgba(59,130,246,0.22)' : 'rgba(59,130,246,0.18)';
  const textPrimary = isLight ? '#0f172a' : '#ffffff';
  const textSecondary = isLight ? '#64748b' : '#9ca3af';
  const inputBg = isLight ? '#f1f5f9' : '#0a1220';

  let lastOutgoingIdx = -1;
  for (let i = thread.length - 1; i >= 0; i--) {
    if (thread[i].senderId === myId) { lastOutgoingIdx = i; break; }
  }
  const showSeen = lastOutgoingIdx >= 0 && thread[lastOutgoingIdx].read;

  // Quiet "invite cancelled / declined / expired" note in the header. Driven
  // by a window event fired from PlayFriendModal so the same note shows up
  // regardless of where the invite was started (messenger, battle page,
  // friend's profile). Auto-clears after a few seconds so it stays unobtrusive.
  const [inviteEndedNote, setInviteEndedNote] = useState(null);
  useEffect(() => {
    if (!friend?.id || typeof window === 'undefined') return undefined;
    const handler = (e) => {
      const d = e?.detail;
      if (!d || d.otherUserId !== friend.id) return;
      const name = friend.username || d.otherUsername || 'They';
      const text =
        d.reason === 'declined' ? `${name} declined the battle invite`
        : d.reason === 'expired' ? 'Battle invite expired'
        : 'Battle invite cancelled';
      setInviteEndedNote(text);
    };
    window.addEventListener('piks:invite:ended', handler);
    return () => window.removeEventListener('piks:invite:ended', handler);
  }, [friend?.id, friend?.username]);
  useEffect(() => {
    if (!inviteEndedNote) return undefined;
    const t = setTimeout(() => setInviteEndedNote(null), 6000);
    return () => clearTimeout(t);
  }, [inviteEndedNote]);

  // Render a message body with @mentions turned into clickable links (IG-style)
  // that navigate to the tagged player's profile. Own-bubble mentions use a
  // light-blue so they read on the blue gradient; received ones use the brand
  // blue. Unresolvable handles still render as links — the click just no-ops if
  // no user matches.
  const renderMessageContent = (text, mine) => {
    if (!text) return text;
    const parts = String(text).split(/(@[A-Za-z0-9_]+)/g);
    return parts.map((part, i) => {
      if (part.startsWith('@') && part.length > 1) {
        return (
          <button
            key={i}
            type="button"
            onClick={(e) => { e.stopPropagation(); resolveAndOpenMention(part.slice(1)); }}
            className="hover:underline"
            style={{
              color: mine ? '#bfdbfe' : '#2563eb',
              fontWeight: 700,
              padding: 0,
              margin: 0,
              border: 0,
              background: 'transparent',
              fontSize: 'inherit',
              fontFamily: 'inherit',
              lineHeight: 'inherit',
              cursor: 'pointer',
              display: 'inline',
              verticalAlign: 'baseline',
            }}
          >
            {part}
          </button>
        );
      }
      return <span key={i}>{part}</span>;
    });
  };

  // Resolve an @handle to a real user and open their profile. The DM partner is
  // matched locally (no round-trip); anyone else is looked up via the user
  // search API (exact, case-insensitive). No match → no navigation.
  const resolveAndOpenMention = async (handle) => {
    const norm = (s) => (s || '').replace(/\s+/g, '').toLowerCase();
    const target = norm(handle);
    if (!target) return;
    if (friend?.id && norm(friend.username) === target) {
      try { router.push(`/profile/${friend.id}`); } catch {}
      return;
    }
    try {
      const res = await fetch(`/api/users/search?q=${encodeURIComponent(handle)}`, { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      const list = Array.isArray(data?.users) ? data.users : [];
      const match = list.find((u) => norm(u.username) === target);
      if (match?.id) { try { router.push(`/profile/${match.id}`); } catch {} }
    } catch {}
  };

  // Insert the picked user's "@username " into the composer, replacing the
  // partial handle the caret is on. Caret span is recomputed from the live
  // input so it stays correct even if the value changed since the search fired.
  const applyMention = (user) => {
    if (!user?.username) return;
    const el = inputRef.current;
    const caret = el ? (el.selectionStart ?? reply.length) : reply.length;
    const det = detectMention(reply, caret);
    const start = det ? det.start : reply.length;
    const end = det ? det.end : reply.length;
    const before = reply.slice(0, start);
    const after = reply.slice(end);
    const inserted = `@${user.username} `;
    const next = `${before}${inserted}${after}`;
    setReply(next);
    setMentionBox(null);
    const pos = (before + inserted).length;
    requestAnimationFrame(() => {
      try { el?.focus(); el?.setSelectionRange(pos, pos); } catch {}
    });
  };

  // Keyboard nav for the mention dropdown. Enter picks (instead of sending),
  // arrows move the highlight, Esc dismisses.
  const handleMentionKeyDown = (e) => {
    if (!mentionBox || !mentionBox.results.length) return;
    const n = mentionBox.results.length;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setMentionBox((b) => (b ? { ...b, activeIndex: (b.activeIndex + 1) % n } : b));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setMentionBox((b) => (b ? { ...b, activeIndex: (b.activeIndex - 1 + n) % n } : b));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      applyMention(mentionBox.results[mentionBox.activeIndex]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setMentionBox(null);
    }
  };

  return (
    // CSS Grid (instead of flex column) for the conversation surface.
    // Grid `auto 1fr auto auto` rows guarantee:
    //   row 1 (header)   — natural height
    //   row 2 (messages) — exactly the remaining vertical space
    //   row 3 (typing)   — natural height
    //   row 4 (composer) — natural height, always visible at the bottom
    // Flex column kept getting bitten by `min-height: auto` propagation
    // bugs in nested flex containers on iOS Safari, which let the
    // composer slip below the parent card's `overflow-hidden` boundary.
    // Grid sizes rows from the parent's available height instead of from
    // child content, so the composer can never be pushed off-screen.
    <div
      className="grid h-full min-h-0 overflow-hidden"
      style={{ gridTemplateRows: 'auto minmax(0, 1fr) auto auto' }}
    >
      <div
        className="flex flex-col"
        style={{ borderBottom: '2.5px solid #0a0a0a' }}
      >
      <div className="flex items-center gap-3 px-3 sm:px-4 py-3">
        {/* Mobile-only back-to-inbox arrow — cartoon themed (chunky
            black outline + offset shadow + press-down) so it matches
            the rest of the new battle UI. Tap target stays 44x44. */}
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            aria-label="Back to messages"
            className="msg-cartoon-btn md:hidden inline-flex items-center justify-center rounded-2xl text-white -ml-1 flex-shrink-0"
            style={{
              width: 44,
              height: 44,
              minWidth: 44,
              minHeight: 44,
              background: 'linear-gradient(180deg,#1f2937,#111827)',
              border: '2.5px solid #0a0a0a',
              boxShadow: '0 3px 0 #0a0a0a',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}
        {/* Whole identity area (avatar + name + status) is one big click
            target that navigates to the friend's profile. Previously the
            avatar and name were each wrapped in their own <Link>, which
            left a dead zone between them and made the affordance feel
            inconsistent. A single Link guarantees the entire row is
            tappable, including any whitespace around the name. */}
        <ProfileHeaderLink friend={friend} textPrimary={textPrimary} textSecondary={textSecondary} />
        <FriendMuteToggle friend={friend} />
        {onStartBattle && (
          <>
            {/* Mobile: square icon-only Start Battle button — cartoon
                themed (chunky black border + offset shadow + bold blue
                gradient) so the trigger feels like part of the same
                product as the modal it opens. */}
            <button
              type="button"
              onClick={() => {
                if (hasActiveMatchup) {
                  if (typeof window !== 'undefined') window.alert(ACTIVE_BATTLE_BLOCK_MESSAGE);
                  return;
                }
                onStartBattle(friend);
              }}
              disabled={hasActiveMatchup}
              className="msg-cartoon-btn sm:hidden inline-flex items-center justify-center w-10 h-10 rounded-2xl text-white flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background: 'linear-gradient(180deg,#3b82f6,#4f46e5)',
                border: '1px solid rgba(0,0,0,0.4)',
                boxShadow: '0 3px 0 #1e3a8a, 0 6px 16px rgba(59,130,246,0.28)',
                textShadow: '0 1px 0 rgba(0,0,0,0.35)',
              }}
              title={hasActiveMatchup ? ACTIVE_BATTLE_BLOCK_MESSAGE : 'Start Battle'}
              aria-label={hasActiveMatchup ? ACTIVE_BATTLE_BLOCK_MESSAGE : `Start battle with ${friend?.username || 'friend'}`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </button>
            {/* Desktop: cartoon-themed pill with icon + label. */}
            <button
              type="button"
              onClick={() => {
                if (hasActiveMatchup) {
                  if (typeof window !== 'undefined') window.alert(ACTIVE_BATTLE_BLOCK_MESSAGE);
                  return;
                }
                onStartBattle(friend);
              }}
              disabled={hasActiveMatchup}
              className="msg-cartoon-btn hidden sm:inline-flex items-center gap-1.5 px-3.5 py-2 rounded-2xl text-xs font-extrabold uppercase tracking-wider text-white flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background: 'linear-gradient(180deg,#3b82f6,#4f46e5)',
                border: '1px solid rgba(0,0,0,0.4)',
                boxShadow: '0 3px 0 #1e3a8a, 0 6px 16px rgba(59,130,246,0.28)',
                textShadow: '0 1px 0 rgba(0,0,0,0.35)',
              }}
              title={hasActiveMatchup ? ACTIVE_BATTLE_BLOCK_MESSAGE : undefined}
              aria-label={hasActiveMatchup ? ACTIVE_BATTLE_BLOCK_MESSAGE : `Start battle with ${friend?.username || 'friend'}`}
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <span>{hasActiveMatchup ? 'In a battle' : 'Start Battle'}</span>
            </button>
          </>
        )}
        {hasActiveMatchup && onStartBattle && (
          <span className="sr-only">{ACTIVE_BATTLE_BLOCK_MESSAGE}</span>
        )}
      </div>
      {inviteEndedNote && (
        <div
          className="px-4 pb-2 -mt-1 text-[11px] flex items-center gap-1.5"
          style={{ color: textSecondary }}
          aria-live="polite"
        >
          <span aria-hidden="true">⚔️</span>
          <span>{inviteEndedNote}</span>
        </div>
      )}
      </div>

      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto overscroll-contain px-4 py-3 space-y-2 min-h-0"
      >
        {loading && (
          <div className="text-center text-xs py-6" style={{ color: textSecondary }}>Loading…</div>
        )}
        {!loading && notFriends && (
          <div className="flex items-center justify-center py-6">
            <NotFriendsNotice
              userId={friend?.id}
              username={friend?.username}
              onFriendAdded={() => { setNotFriends(false); setLoadError(null); setReloadKey((k) => k + 1); }}
            />
          </div>
        )}
        {!loading && !notFriends && loadError && (
          <div className="text-center text-xs py-6 text-red-400">{loadError}</div>
        )}
        {!loading && !notFriends && !loadError && thread.length === 0 && (
          <div className="text-center text-xs py-6" style={{ color: textSecondary }}>
            No messages yet. Say hi!
          </div>
        )}
        {!loading && !notFriends && !loadError && thread.map((m, idx) => {
          const mine = m.senderId === myId;
          const isDeleting = deletingIds.has(m.id);
          return (
          <div
            key={m.id}
            className={`flex flex-col ${mine ? 'items-end' : 'items-start'}`}
          >
            {/* Bubble row. A "•••" actions menu sits on the outer edge of
                the bubble (LEFT for own right-aligned messages, RIGHT for
                received ones). The `group` class lets desktop hover reveal
                it; on touch (no hover) it stays at low opacity so it's
                always tappable — per the no-hover-on-touch app preference. */}
            <div
              className={`flex items-center gap-1.5 max-w-full group ${
                mine ? 'flex-row' : 'flex-row-reverse'
              }`}
            >
              <MessageActionsMenu
                mine={mine}
                disabled={isDeleting}
                onUnsend={() => handleDeleteMessage(m.id)}
                onDeleteForMe={() => handleDeleteForMe(m.id)}
              />
              {/* Cartoon-themed bubbles — chunky black border + offset
                  shadow on both sides. Mine uses a bold blue gradient
                  (matches the "Start Battle" CTA), theirs uses a dark
                  slate gradient so the contrast still reads cleanly. */}
              <div
                className={`max-w-[80%] px-3.5 py-2 rounded-2xl text-sm leading-snug break-words ${
                  mine
                    ? 'text-white rounded-br-sm'
                    : `rounded-bl-sm ${isLight ? '' : 'text-white'}`
                }`}
                style={{
                  ...(mine
                    ? {
                        background: 'linear-gradient(180deg,#3b82f6,#2563eb)',
                        border: '2.5px solid #0a0a0a',
                        boxShadow: '0 3px 0 #0a0a0a, 0 0 14px rgba(59,130,246,0.35)',
                        textShadow: '0 1px 0 rgba(0,0,0,0.25)',
                      }
                    : isLight
                    ? {
                        background: 'linear-gradient(180deg,#ffffff,#f1f5f9)',
                        border: '2.5px solid #0a0a0a',
                        boxShadow: '0 3px 0 #0a0a0a',
                        color: '#0f172a',
                      }
                    : {
                        background: 'linear-gradient(180deg,#1f2937,#111827)',
                        border: '2.5px solid #0a0a0a',
                        boxShadow: '0 3px 0 #0a0a0a',
                      }),
                  ...(isDeleting ? { opacity: 0.5 } : null),
                }}
              >
                {m.messageType === 'voice' && m.attachmentUrl ? (
                  <VoiceBubble
                    url={m.attachmentUrl}
                    durationMs={m.attachmentDurationMs}
                    peaks={m.attachmentPeaks}
                    messageId={m.id}
                    mine={mine}
                  />
                ) : (m.messageType === 'shared_battle' || m.messageType === 'shared_post' || m.messageType === 'shared_result') ? (
                  <SharedItemBubble
                    raw={m.content}
                    mine={mine}
                    senderId={mine ? myId : friend?.id}
                    senderUsername={mine ? undefined : friend?.username}
                    onNavigate={(href) => { try { router.push(href); } catch {} }}
                  />
                ) : (
                  renderMessageContent(m.content, mine)
                )}
              </div>
            </div>
            {showSeen && idx === lastOutgoingIdx && (
              <p
                className="text-[10px] mt-0.5 mr-0.5"
                style={{ color: textSecondary }}
                suppressHydrationWarning
              >
                {thread[lastOutgoingIdx].readAt
                  ? `Seen ${formatSeenAgo(thread[lastOutgoingIdx].readAt)}`
                  : 'Seen'}
              </p>
            )}
          </div>
          );
        })}
      </div>

      <div className="h-5 px-4 flex-shrink-0" aria-live="polite">
        {isTyping && (
          <div className="flex items-center gap-1.5 text-[11px] italic text-blue-300">
            <span className="flex gap-0.5">
              <span className="w-1 h-1 rounded-full bg-blue-400 animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="w-1 h-1 rounded-full bg-blue-400 animate-bounce" style={{ animationDelay: '120ms' }} />
              <span className="w-1 h-1 rounded-full bg-blue-400 animate-bounce" style={{ animationDelay: '240ms' }} />
            </span>
            <span>{friend.username || 'Friend'} is typing…</span>
          </div>
        )}
      </div>

      {!loadError && !notFriends && (
        <form
          onSubmit={handleSend}
          className="p-3 flex-shrink-0"
          style={{
            borderTop: `1px solid ${cardBorder}`,
            paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))',
          }}
        >
          {voicePreview && !recording ? (
            <div
              className="flex items-center gap-2 px-3 py-2 rounded-lg flex-wrap"
              style={{
                backgroundColor: inputBg,
                border: '1px solid rgba(59,130,246,0.4)',
              }}
            >
              <VoiceWaveform
                blob={voicePreview.blob}
                url={voicePreview.url}
                durationMs={voicePreview.durationMs}
                variant="preview"
                trimStartMs={voicePreview.trimStartMs ?? 0}
                trimEndMs={voicePreview.trimEndMs ?? voicePreview.durationMs}
                onTrimChange={setVoicePreviewTrim}
                peaks={voicePreview.peaks}
                onPeaks={setVoicePreviewPeaks}
              />
              <div className="ml-auto flex gap-2 flex-shrink-0">
                <button
                  type="button"
                  onClick={handleRerecordPreview}
                  disabled={sending}
                  aria-label="Re-record voice note"
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold disabled:opacity-50"
                  style={{
                    backgroundColor: isLight ? '#fef2f2' : '#1f1f1f',
                    color: isLight ? '#dc2626' : '#fca5a5',
                    border: '1px solid rgba(239,68,68,0.4)',
                  }}
                >
                  Re-record
                </button>
                {resumableTake && (
                  <button
                    type="button"
                    onClick={handleContinueRecording}
                    disabled={sending}
                    aria-label="Continue recording from where you left off"
                    title="Add more to this recording"
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold disabled:opacity-50"
                    style={{
                      backgroundColor: isLight ? '#eff6ff' : '#0f1622',
                      color: isLight ? '#2563eb' : '#bfdbfe',
                      border: '1px solid rgba(59,130,246,0.45)',
                    }}
                  >
                    Continue
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleSendPreview}
                  disabled={sending}
                  className="px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-blue-500 disabled:opacity-50"
                  style={{ boxShadow: '0 0 14px rgba(59,130,246,0.5)' }}
                >
                  {sending ? '…' : 'Send'}
                </button>
              </div>
            </div>
          ) : processingPreview ? (
            // Brief bridge state on the segment-stitch (Safari/iOS) path
            // between tapping Done and the concatenated WAV preview being
            // ready. Without this the composer would snap back to the text
            // input mid-decode, which would look like the take got dropped.
            <div
              className="flex items-center gap-2 px-3 py-2 rounded-lg"
              style={{
                backgroundColor: inputBg,
                border: '1px solid rgba(59,130,246,0.4)',
                color: textSecondary,
              }}
              aria-live="polite"
            >
              <span
                className="inline-block w-2 h-2 rounded-full bg-blue-400"
                style={{ animation: 'piksRecPulse 1s ease-in-out infinite' }}
              />
              <span className="text-xs font-medium">Processing…</span>
              <style>{`@keyframes piksRecPulse{0%,100%{opacity:1}50%{opacity:0.35}}`}</style>
            </div>
          ) : recording ? (
            <>
            <div
              className="flex items-center gap-2 px-3 py-2 rounded-lg"
              style={{
                backgroundColor: inputBg,
                border: paused
                  ? '1px solid rgba(156,163,175,0.4)'
                  : '1px solid rgba(239,68,68,0.4)',
              }}
            >
              <span
                className={`inline-block w-2 h-2 rounded-full ${paused ? 'bg-gray-400' : 'bg-red-500'}`}
                style={
                  paused
                    ? undefined
                    : { boxShadow: '0 0 10px rgba(239,68,68,0.7)', animation: 'piksRecPulse 1s ease-in-out infinite' }
                }
              />
              <span
                className="text-xs font-medium"
                style={{ color: paused ? '#9ca3af' : '#fca5a5' }}
              >
                {paused ? 'Paused' : 'Recording'}
              </span>
              <div
                className="flex items-end gap-[2px] h-3.5"
                aria-hidden="true"
                style={{ minWidth: 56 }}
              >
                {audioLevels.map((lvl, i) => {
                  const h = Math.max(2, Math.round(lvl * 14));
                  return (
                    <span
                      key={i}
                      className={`block w-[2px] rounded-sm ${paused ? 'bg-gray-500' : 'bg-red-400'}`}
                      style={{
                        height: `${h}px`,
                        opacity: paused ? 0.5 : 0.55 + lvl * 0.45,
                        transition: 'height 80ms linear, opacity 80ms linear',
                      }}
                    />
                  );
                })}
              </div>
              <span className="text-xs tabular-nums" style={{ color: textSecondary }}>
                {formatDuration(recordElapsed)} / {formatDuration(MAX_VOICE_MS)}
              </span>
              <div className="ml-auto flex gap-2">
                <button
                  type="button"
                  onClick={handleCancelRecording}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold"
                  style={{ backgroundColor: '#1f1f1f', color: '#e5e7eb', border: `1px solid ${cardBorder}` }}
                >
                  Cancel
                </button>
                {pauseSupported && (
                  paused ? (
                    <button
                      type="button"
                      onClick={handleResumeRecording}
                      aria-label="Resume recording"
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white"
                      style={{
                        backgroundColor: '#7f1d1d',
                        border: '1px solid rgba(239,68,68,0.5)',
                      }}
                    >
                      Resume
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handlePauseRecording}
                      aria-label="Pause recording"
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold"
                      style={{
                        backgroundColor: '#1f1f1f',
                        color: '#e5e7eb',
                        border: `1px solid ${cardBorder}`,
                      }}
                    >
                      Pause
                    </button>
                  )
                )}
                <button
                  type="button"
                  onClick={handleStopRecording}
                  aria-label="Finish recording and preview"
                  className="px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-blue-500"
                  style={{ boxShadow: '0 0 14px rgba(59,130,246,0.5)' }}
                >
                  Done
                </button>
              </div>
              <style>{`@keyframes piksRecPulse{0%,100%{opacity:1}50%{opacity:0.35}}`}</style>
            </div>
            {paused && pauseReason === 'tab' && (
              <p
                className="mt-1 text-[11px] flex items-start gap-1.5"
                style={{ color: textSecondary }}
                aria-live="polite"
              >
                <span aria-hidden="true">⏸</span>
                <span>Paused because you switched tabs — tap Resume to continue.</span>
              </p>
            )}
            {paused && pauseReason === 'system' && (
              <p
                className="mt-1 text-[11px] flex items-start gap-1.5"
                style={{ color: '#fcd34d' }}
                aria-live="polite"
              >
                <span aria-hidden="true">⚠</span>
                <span>Paused — your microphone was interrupted. Tap Resume to continue.</span>
              </p>
            )}
            </>
          ) : (
            /* Cartoon-themed composer row — text input has chunky black
               border + soft inset shadow, mic + send have the same
               offset-shadow press behavior as the rest of the slip. */
            <div className="relative">
              {mentionBox && mentionBox.results.length > 0 && (
                <div
                  className="absolute left-0 right-0 bottom-full mb-2 rounded-2xl overflow-hidden z-30"
                  style={{
                    backgroundColor: isLight ? '#ffffff' : '#0f172a',
                    border: '2.5px solid #0a0a0a',
                    boxShadow: '0 4px 0 #0a0a0a',
                  }}
                >
                  {mentionBox.results.map((u, i) => (
                    <button
                      key={u.id}
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); applyMention(u); }}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-left"
                      style={{
                        backgroundColor: i === mentionBox.activeIndex
                          ? (isLight ? 'rgba(37,99,235,0.10)' : 'rgba(59,130,246,0.18)')
                          : 'transparent',
                      }}
                    >
                      <UserAvatar username={u.username} avatar={u.avatar} size={26} />
                      <span className="text-sm font-semibold truncate" style={{ color: textPrimary }}>
                        @{u.username}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={reply}
                onChange={handleReplyChange}
                onKeyDown={handleMentionKeyDown}
                placeholder="Write a message…"
                className="flex-1 min-w-0 px-3.5 py-2.5 rounded-2xl focus:outline-none"
                style={{
                  backgroundColor: inputBg,
                  border: '2.5px solid #0a0a0a',
                  color: textPrimary,
                  boxShadow: 'inset 0 2px 0 rgba(0,0,0,0.4)',
                  fontSize: 16,
                  lineHeight: '20px',
                }}
                maxLength={1000}
                disabled={sending}
              />
              {!reply.trim() ? (
                <button
                  type="button"
                  onClick={handleStartRecording}
                  disabled={sending}
                  aria-label="Record voice message"
                  className="msg-cartoon-btn flex items-center justify-center w-11 px-0 rounded-2xl text-white disabled:opacity-50"
                  style={{
                    background: 'linear-gradient(180deg,#1f2937,#111827)',
                    border: '2.5px solid #0a0a0a',
                    boxShadow: '0 3px 0 #0a0a0a',
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="9" y="2" width="6" height="12" rx="3" />
                    <path d="M5 10v2a7 7 0 0 0 14 0v-2" />
                    <line x1="12" y1="19" x2="12" y2="22" />
                  </svg>
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={!reply.trim() || sending}
                  className="msg-cartoon-btn px-4 py-2 disabled:opacity-50 text-white text-xs font-extrabold uppercase tracking-wider rounded-2xl"
                  style={{
                    background: 'linear-gradient(180deg,#3b82f6,#2563eb)',
                    border: '2.5px solid #0a0a0a',
                    boxShadow: !reply.trim() || sending
                      ? '0 3px 0 #0a0a0a'
                      : '0 3px 0 #0a0a0a, 0 0 14px rgba(59,130,246,0.5)',
                    textShadow: '0 1px 0 rgba(0,0,0,0.35)',
                  }}
                >
                  {sending ? '…' : 'Send'}
                </button>
              )}
              </div>
            </div>
          )}
          {(voiceError || sendError) && (
            <div role="alert" className="text-red-400 text-[11px] mt-1">{voiceError || sendError}</div>
          )}
        </form>
      )}
    </div>
  );
}

function NotFriendsNotice({ userId, username, onFriendAdded }) {
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [requestStatus, setRequestStatus] = useState(null);

  const cardBorder = isLight ? 'rgba(59,130,246,0.22)' : 'rgba(59,130,246,0.18)';
  const textPrimary = isLight ? '#0f172a' : '#ffffff';
  const textSecondary = isLight ? '#64748b' : '#9ca3af';
  const innerBg = isLight ? '#f1f5f9' : '#0a1220';

  const handleAdd = async () => {
    if (sending) return;
    setSending(true);
    setSendError(null);
    try {
      const res = await fetch('/api/friends', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ friendId: userId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (/already friends/i.test(data?.error || '')) {
          onFriendAdded?.();
          return;
        }
        setSendError(data?.error || 'Could not send friend request.');
        return;
      }
      const status = data?.status === 'accepted' ? 'accepted' : 'pending';
      setRequestStatus(status);
      if (status === 'accepted') onFriendAdded?.();
    } catch {
      setSendError('Could not send friend request.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="w-full max-w-sm rounded-xl p-5 text-center"
      style={{ backgroundColor: innerBg, border: `1px solid ${cardBorder}` }}
    >
      <div className="text-sm font-semibold mb-1" style={{ color: textPrimary }}>
        You're not friends yet
      </div>
      <p className="text-xs mb-4" style={{ color: textSecondary }}>
        You can only message friends — send {username || 'this player'} a friend request first.
      </p>
      {requestStatus === 'pending' && (
        <div className="text-xs mb-3" style={{ color: textSecondary }}>
          Friend request sent. You'll be able to message them once they accept.
        </div>
      )}
      {requestStatus === 'accepted' && (
        <div className="text-xs mb-3 text-blue-400">You're now friends!</div>
      )}
      {!requestStatus && (
        <button
          type="button"
          onClick={handleAdd}
          disabled={sending}
          className="bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-xs font-bold px-4 py-2 rounded-lg"
        >
          {sending ? 'Sending…' : 'Add friend'}
        </button>
      )}
      {sendError && (
        <div className="text-red-400 text-[11px] mt-2">{sendError}</div>
      )}
    </div>
  );
}

function NotFriendsCard({ userId, onFriendAdded, onBack }) {
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setProfile(null);
    (async () => {
      try {
        const res = await fetch(`/api/profiles/${userId}`, { credentials: 'include' });
        if (!res.ok) {
          if (!cancelled) setLoadError(res.status === 404 ? 'User not found.' : 'Could not load user.');
          return;
        }
        const data = await res.json();
        if (!cancelled) setProfile(data);
      } catch {
        if (!cancelled) setLoadError('Could not load user.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  const cardBorder = isLight ? 'rgba(59,130,246,0.22)' : 'rgba(59,130,246,0.18)';
  const textPrimary = isLight ? '#0f172a' : '#ffffff';
  const textSecondary = isLight ? '#64748b' : '#9ca3af';
  const cardBg = isLight ? '#ffffff' : '#0a0a0a';

  return (
    <div className="flex flex-col h-full min-h-0">
      <div
        className="flex items-center gap-3 px-3 sm:px-4 py-3 flex-shrink-0"
        style={{ borderBottom: `1px solid ${cardBorder}` }}
      >
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            aria-label="Back to messages"
            className="md:hidden inline-flex items-center justify-center rounded-lg text-blue-300 active:text-white -ml-1 flex-shrink-0"
            style={{
              width: 44,
              height: 44,
              minWidth: 44,
              minHeight: 44,
              backgroundColor: 'rgba(59,130,246,0.12)',
              border: '1px solid rgba(59,130,246,0.35)',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}
        <Avatar
          user={profile || {}}
          isOnline={profile?.isOnline ?? isUserOnline(profile?.lastSeenAt)}
          onlineDotBorderColor={cardBg}
        />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold truncate" style={{ color: textPrimary }}>
            {loading ? 'Loading…' : (profile?.username || 'Player')}
          </div>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center px-6 py-8 min-h-0">
        {loading ? (
          <div className="text-xs" style={{ color: textSecondary }}>Loading…</div>
        ) : loadError ? (
          <div className="text-xs text-red-400 text-center">{loadError}</div>
        ) : (
          <NotFriendsNotice
            userId={userId}
            username={profile?.username}
            onFriendAdded={onFriendAdded}
          />
        )}
      </div>
    </div>
  );
}

function applyIncomingMessage(prev, msg, myId, selectedId) {
  if (!msg || !myId) return prev;
  const otherId = msg.senderId === myId ? msg.receiverId : msg.senderId;
  if (!otherId) return prev;
  const idx = prev.findIndex((c) => c.friend?.id === otherId);
  if (idx === -1) return null;
  const target = prev[idx];
  const existingTs = target.lastMessage?.createdAt
    ? new Date(target.lastMessage.createdAt).getTime()
    : 0;
  const incomingTs = msg.createdAt ? new Date(msg.createdAt).getTime() : Date.now();
  if (incomingTs < existingTs) return prev;
  const fromMe = msg.senderId === myId;
  const next = prev.slice();
  next[idx] = {
    ...target,
    lastMessage: {
      id: msg.id,
      senderId: msg.senderId,
      receiverId: msg.receiverId,
      content: msg.content,
      preview: (msg.content || '').slice(0, 120),
      createdAt:
        typeof msg.createdAt === 'string'
          ? msg.createdAt
          : new Date(incomingTs).toISOString(),
      fromMe,
      unread: !fromMe && otherId !== selectedId,
    },
  };
  return next;
}

export default function MessagesPanel({
  selectedId,
  onSelect,
  ctx,
  myId,
  variant = 'card', // 'card' | 'fullpage'
  minHeight = 520,
  onStartBattle,
}) {
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [friendsError, setFriendsError] = useState(false);

  const liveUnreadIds = useMemo(() => {
    const s = new Set();
    (ctx.unreadMessages || []).forEach((m) => { if (m.sender?.id) s.add(m.sender.id); });
    return s;
  }, [ctx.unreadMessages]);

  const fetchConversations = useCallback(async () => {
    try {
      const res = await fetch('/api/messages/conversations', { credentials: 'include' });
      if (!res.ok) {
        setFriendsError(true);
        return;
      }
      const data = await res.json();
      setConversations(data.conversations || []);
      setFriendsError(false);
    } catch {
      setFriendsError(true);
    } finally { setLoading(false); }
  }, []);

  const loadFriends = fetchConversations;

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  const unreadKey = useMemo(
    () => Array.from(liveUnreadIds).sort().join(','),
    [liveUnreadIds]
  );
  useEffect(() => {
    if (loading) return;
    fetchConversations();
    // Intentionally NOT depending on `selectedId` — clicking a conversation
    // shouldn't refetch the entire inbox. The `markMessagesRead` call in
    // the parent already updates `unreadKey`, which triggers a refresh
    // when needed. Re-fetching on every selection caused a perceptible
    // pause when switching threads.
  }, [unreadKey, fetchConversations]);

  const selectedIdRef = useRef(selectedId);
  useEffect(() => { selectedIdRef.current = selectedId; }, [selectedId]);
  const myIdRef = useRef(myId);
  useEffect(() => { myIdRef.current = myId; }, [myId]);
  const conversationsRef = useRef(conversations);
  useEffect(() => { conversationsRef.current = conversations; }, [conversations]);
  const seenMessageIdsRef = useRef(new Set());

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handler = (e) => {
      const msg = e?.detail;
      if (!msg) return;
      if (msg.id) {
        if (seenMessageIdsRef.current.has(msg.id)) return;
        seenMessageIdsRef.current.add(msg.id);
        if (seenMessageIdsRef.current.size > 200) {
          const arr = Array.from(seenMessageIdsRef.current);
          seenMessageIdsRef.current = new Set(arr.slice(-100));
        }
      }
      const next = applyIncomingMessage(
        conversationsRef.current,
        msg,
        myIdRef.current,
        selectedIdRef.current
      );
      if (next === null) {
        fetchConversations();
        return;
      }
      if (next !== conversationsRef.current) setConversations(next);
    };
    window.addEventListener('piks:message:new', handler);
    return () => window.removeEventListener('piks:message:new', handler);
  }, [fetchConversations]);

  // Background backfill of waveform peaks for legacy voice notes whose row
  // would otherwise render the "🎤 Voice message" text placeholder. The
  // bubble-side `VoiceWaveform` already decodes + persists peaks when a
  // thread is opened, but threads the user never taps would keep the
  // placeholder forever. To make the inbox look uniform we walk the
  // conversation list, fetch + decode the latest voice attachment in any
  // such thread, POST the result so the persistence is durable across page
  // loads, and patch local state so the row swaps to the static waveform
  // immediately. Each message id is attempted at most once per session.
  const inboxBackfillAttemptedRef = useRef(new Set());
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return undefined;

    const candidates = conversations.filter((c) => {
      const last = c.lastMessage;
      if (!last || last.messageType !== 'voice') return false;
      if (!last.id || !last.attachmentUrl) return false;
      if (Array.isArray(last.attachmentPeaks) && last.attachmentPeaks.length > 0) return false;
      if (inboxBackfillAttemptedRef.current.has(last.id)) return false;
      return true;
    });
    if (candidates.length === 0) return undefined;

    let cancelled = false;
    // Decode candidates one at a time so we don't kick off N parallel audio
    // fetches the moment the inbox loads — the work is best-effort and the
    // bubble's own decode path remains the user-visible fast path when a
    // thread is opened. Wait for browser idle so the inbox itself paints
    // first.
    const idle = scheduleIdle(async () => {
      for (const c of candidates) {
        if (cancelled) return;
        const last = c.lastMessage;
        if (!last) continue;
        const messageId = last.id;
        const url = last.attachmentUrl;
        if (inboxBackfillAttemptedRef.current.has(messageId)) continue;
        // Mark up-front so a re-render mid-decode doesn't re-queue this
        // message. The set persists for the lifetime of the panel mount.
        inboxBackfillAttemptedRef.current.add(messageId);

        // Patch the matching conversation row with freshly decoded peaks.
        // Returns `prev` unchanged when the target row already carries
        // peaks — important because this effect depends on `conversations`,
        // and an unchanged reference makes React bail out of the re-render
        // (and the resulting effect cleanup/restart) so the loop can keep
        // draining sequentially instead of being torn down per success.
        const patchPeaks = (peaksArr) => {
          if (cancelled) return;
          setConversations((prev) => {
            let changed = false;
            const next = prev.map((conv) => {
              if (!conv.lastMessage || conv.lastMessage.id !== messageId) return conv;
              const existing = conv.lastMessage.attachmentPeaks;
              if (Array.isArray(existing) && existing.length > 0) return conv;
              changed = true;
              return {
                ...conv,
                lastMessage: { ...conv.lastMessage, attachmentPeaks: peaksArr },
              };
            });
            return changed ? next : prev;
          });
        };

        // If a sibling decode (e.g. opening this thread earlier in the
        // session) already populated the URL-keyed cache we can skip the
        // fetch entirely and just patch the row + persist.
        const cached = getCachedWaveformPeaks(url);
        if (Array.isArray(cached) && cached.length > 0) {
          patchPeaks(cached);
          backfillPeaksToServer(messageId, cached);
          continue;
        }

        let audioCtx = null;
        try {
          const res = await fetch(url, { credentials: 'include' });
          if (!res.ok) continue;
          const arrBuf = await res.arrayBuffer();
          if (cancelled) return;
          audioCtx = new Ctx();
          const decoded = await audioCtx.decodeAudioData(arrBuf.slice(0));
          if (cancelled) return;
          const norm = extractPeaksFromAudioBuffer(decoded);
          if (!Array.isArray(norm) || norm.length === 0) continue;
          // Cache so a subsequent thread open renders the bubble instantly
          // from memory instead of repeating the decode.
          setCachedWaveformPeaks(url, norm);
          // Fire-and-forget the persistence POST (the endpoint refuses to
          // overwrite a row that already has peaks, so a last-write-wins
          // race with the bubble path is harmless).
          backfillPeaksToServer(messageId, norm);
          patchPeaks(norm);
        } catch {
          // Failures are silent — the row keeps its text placeholder and
          // we won't retry this id again until the next session, matching
          // how the bubble's own decode path treats unsupported codecs.
        } finally {
          if (audioCtx) { try { await audioCtx.close(); } catch {} }
        }
      }
    });
    return () => {
      cancelled = true;
      idle.cancel();
    };
  }, [conversations]);

  const sorted = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? conversations.filter((c) => (c.friend?.username || '').toLowerCase().includes(q))
      : conversations;
    return [...filtered].sort((a, b) => {
      const au = (a.lastMessage?.unread || liveUnreadIds.has(a.friend.id)) ? 1 : 0;
      const bu = (b.lastMessage?.unread || liveUnreadIds.has(b.friend.id)) ? 1 : 0;
      if (au !== bu) return bu - au;

      const at = a.lastMessage?.createdAt ? new Date(a.lastMessage.createdAt).getTime() : 0;
      const bt = b.lastMessage?.createdAt ? new Date(b.lastMessage.createdAt).getTime() : 0;
      if (at !== bt) return bt - at;

      return (a.friend?.username || '').localeCompare(b.friend?.username || '');
    });
  }, [conversations, query, liveUnreadIds]);

  const selectedFriend = useMemo(
    () => conversations.find((c) => c.friend?.id === selectedId)?.friend || null,
    [conversations, selectedId]
  );

  const cardBg = isLight ? '#ffffff' : '#0a0a0a';
  const cardBorder = isLight ? 'rgba(59,130,246,0.28)' : 'rgba(59,130,246,0.22)';
  const textPrimary = isLight ? '#0f172a' : '#ffffff';
  const textSecondary = isLight ? '#64748b' : '#9ca3af';
  const inputBg = isLight ? '#f1f5f9' : '#0a1220';
  const rowHover = isLight ? 'rgba(59,130,246,0.08)' : 'rgba(59,130,246,0.06)';
  const rowSelected = isLight ? 'rgba(59,130,246,0.14)' : 'rgba(59,130,246,0.18)';
  // Cartoon panel chrome — chunky 2.5px black border + offset shadow
  // matches PikSlip / PlayFriendModal / PrivateMatchModal so the
  // Messenger feels part of the same family. Kept as a separate
  // constant from `cardBorder` (the soft blue accent) so the inbox
  // row internals can keep their original subtler accent.
  const panelBorder = '2.5px solid #0a0a0a';
  const panelShadow = '0 4px 0 #0a0a0a, 0 0 24px -8px rgba(59,130,246,0.35)';

  const isFullpage = variant === 'fullpage';
  const containerStyle = isFullpage
    ? { backgroundColor: cardBg, border: panelBorder, height: '100%', boxShadow: panelShadow }
    : { backgroundColor: cardBg, border: panelBorder, minHeight, boxShadow: panelShadow };

  const sidebarMaxHeight = isFullpage ? undefined : 480;

  return (
    <div
      className={`rounded-2xl overflow-hidden flex flex-col md:flex-row ${isFullpage ? 'h-full' : ''}`}
      style={containerStyle}
    >
      <div
        className={`md:w-72 flex-shrink-0 flex flex-col ${selectedId ? 'hidden md:flex' : 'flex'}`}
        style={{ borderRight: '2.5px solid #0a0a0a' }}
      >
        <div
          className="px-4 py-3 flex-shrink-0"
          style={{
            borderBottom: '2.5px solid #0a0a0a',
            background: isLight
              ? 'linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)'
              : 'linear-gradient(180deg, #0d0d0d 0%, #0a0a0a 100%)',
          }}
        >
          <div
            className="font-extrabold uppercase mb-2"
            style={{
              color: '#3b82f6',
              fontSize: 11,
              letterSpacing: '0.18em',
              textShadow: '0 1px 0 #0a0a0a',
            }}
          >
            Messages
          </div>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search friends…"
            className="w-full px-3 py-2 rounded-xl text-xs font-semibold focus:outline-none"
            style={{
              backgroundColor: inputBg,
              border: '2.5px solid #0a0a0a',
              boxShadow: '0 2px 0 #0a0a0a, inset 0 2px 0 rgba(0,0,0,0.4)',
              color: textPrimary,
            }}
          />
        </div>
        <div
          className="overflow-y-auto flex-1 min-h-0"
          style={sidebarMaxHeight ? { maxHeight: sidebarMaxHeight } : undefined}
        >
          {loading && (
            <div className="text-center text-xs py-6" style={{ color: textSecondary }}>Loading…</div>
          )}
          {!loading && sorted.length === 0 && (
            <div className="text-center text-xs py-8 px-4" style={{ color: textSecondary }}>
              {query ? 'No matches.' : 'No friends yet. Add friends to start messaging.'}
            </div>
          )}
          {!loading && sorted.map((c) => {
            const f = c.friend;
            const last = c.lastMessage;
            const isSelected = selectedId === f.id;
            const unread = (last?.unread || liveUnreadIds.has(f.id)) && !isSelected;
            const isTyping = ctx.typingSenderIds?.has?.(f.id);
            // For voice notes whose peaks are persisted alongside the message
            // we render a tiny static waveform + duration in place of the
            // generic "🎤 Voice message" placeholder so users can tell at a
            // glance which thread has a fresh voice note. Older voice notes
            // (no peaks stored yet) keep the placeholder text.
            const lastIsVoiceWithPeaks = !!(
              last
              && last.messageType === 'voice'
              && Array.isArray(last.attachmentPeaks)
              && last.attachmentPeaks.length > 0
            );
            // For shared_battle/shared_post, the raw content is JSON —
            // render a friendly label instead of dumping `{v:1,...}` into
            // the inbox row.
            let rawPreview = last?.preview || last?.content || '';
            if (last && (last.messageType === 'shared_battle' || last.messageType === 'shared_post' || last.messageType === 'shared_result')) {
              rawPreview = last.messageType === 'shared_battle'
                ? '📺 Shared a live battle'
                : (last.messageType === 'shared_result' ? '🏆 Shared a battle result' : '📝 Shared a post');
            }
            const previewText = last
              ? `${last.fromMe ? 'You: ' : ''}${rawPreview}`
              : `${f.battleWins || 0}W-${f.battleLosses || 0}L`;
            return (
              /* Cartoon-themed inbox row — selected gets a thicker
                 black left bar to read like the chunky outline used
                 elsewhere; unread badges already use the right glow. */
              <button
                key={f.id}
                type="button"
                onClick={() => onSelect(f.id)}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors relative"
                style={{
                  // Selected: gradient tint pop + bold inset blue
                  // chunky bar on the left so the row reads as
                  // "active conversation" the same way the buy-in /
                  // game-mode tiles read selected elsewhere.
                  background: isSelected
                    ? 'linear-gradient(180deg, rgba(59,130,246,0.22), rgba(59,130,246,0.10))'
                    : 'transparent',
                  borderBottom: '2px solid #0a0a0a',
                  borderLeft: isSelected ? '5px solid #0a0a0a' : '5px solid transparent',
                  boxShadow: isSelected
                    ? 'inset 5px 0 0 #3b82f6, inset 0 0 18px -8px rgba(59,130,246,0.6)'
                    : 'none',
                }}
                onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = rowHover; }}
                onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <UserAvatar
                  user={f}
                  size={36}
                  isOnline={f.isOnline ?? isUserOnline(f.lastSeenAt)}
                  onlineDotBorderColor={isSelected ? rowSelected : cardBg}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-sm truncate flex-1 min-w-0 ${unread ? 'font-bold' : 'font-medium'}`}
                      style={{ color: textPrimary }}
                    >
                      {f.username || 'Player'}
                    </span>
                    {last?.createdAt && (
                      <span
                        className="text-[10px] flex-shrink-0"
                        style={{ color: textSecondary }}
                        suppressHydrationWarning
                      >
                        {timeAgo(last.createdAt)}
                      </span>
                    )}
                    {unread && (c.unreadCount > 0 ? (
                      <span
                        className="min-w-[18px] h-[18px] px-1 text-[10px] font-bold rounded-full bg-blue-500 text-white flex-shrink-0 flex items-center justify-center"
                        style={{ boxShadow: '0 0 8px rgba(59,130,246,0.65)' }}
                      >
                        {c.unreadCount > 9 ? '9+' : c.unreadCount}
                      </span>
                    ) : (
                      <span
                        className="w-2 h-2 rounded-full bg-blue-400 flex-shrink-0"
                        style={{ boxShadow: '0 0 8px rgba(59,130,246,0.85)' }}
                      />
                    ))}
                  </div>
                  <div
                    className={`text-[11px] truncate ${unread ? 'font-semibold' : ''}`}
                    style={{ color: unread ? textPrimary : textSecondary }}
                  >
                    {isTyping ? (
                      <span className="text-blue-300 italic">typing…</span>
                    ) : lastIsVoiceWithPeaks ? (
                      <InboxVoicePreview
                        peaks={last.attachmentPeaks}
                        durationMs={last.attachmentDurationMs}
                        fromMe={last.fromMe}
                      />
                    ) : (
                      previewText
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* `min-h-0` is REQUIRED here. Without it, this flex item inherits
          the default `min-height: auto`, which means its measured height
          can grow to accommodate its child (`ConversationThread`) instead
          of being clamped to the available flex space. On iOS Safari at
          `100svh`, that overflow gets clipped by the card's
          `overflow-hidden`, hiding the composer below the visible card
          edge. With `min-h-0` here, this pane can shrink to the actual
          available space and the composer stays visible. */}
      <div className={`flex-1 min-w-0 min-h-0 ${selectedId ? 'flex' : 'hidden md:flex'} flex-col`}>
        {selectedFriend ? (
          <ConversationThread
            key={selectedFriend.id}
            friend={selectedFriend}
            ctx={ctx}
            myId={myId}
            onStartBattle={onStartBattle}
            onBack={() => onSelect(null)}
          />
        ) : selectedId && !loading && !friendsError ? (
          <NotFriendsCard
            key={selectedId}
            userId={selectedId}
            onFriendAdded={loadFriends}
            onBack={() => onSelect(null)}
          />
        ) : selectedId && friendsError ? (
          <div
            className="flex-1 flex items-center justify-center text-sm px-6 text-center text-red-400"
            style={{ minHeight: 320 }}
          >
            Could not load your friends list. Please try again.
          </div>
        ) : selectedId ? (
          <div
            className="flex-1 flex items-center justify-center text-sm px-6 text-center"
            style={{ color: textSecondary, minHeight: 320 }}
          >
            Loading…
          </div>
        ) : (
          <div
            className="flex-1 flex items-center justify-center text-sm px-6 text-center"
            style={{ color: textSecondary, minHeight: 320 }}
          >
            Select a friend to start chatting.
          </div>
        )}
      </div>
    </div>
  );
}
